<template>
  <div class="container-fluid py-4">
    
    <!-- HEADER CONTROLS (View Switcher Button) -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h5 class="mb-0 text-dark font-weight-bolder">
          {{ currentView === 'services' ? 'Service Queue System' : (currentView === 'board' ? 'Counter Board (Drag & Drop)' : 'Token Management') }}
        </h5>
        <p class="text-xs text-secondary mb-0">
          <span v-if="currentView === 'services'">Select a service below to generate a new token</span>
          <span v-else-if="currentView === 'board'">{{ canDragDrop ? 'Drag and drop tokens between counters to reassign' : 'View active counter queue distribution' }}</span>
          <span v-else>Manage and serve active daily tokens</span>
        </p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <!-- TOGGLE BUTTON: SWITCH BETWEEN SERVICES GRID & ALL TOKENS TABLE -->
        <button 
          v-if="currentView === 'services'" 
          class="btn bg-gradient-primary mb-0 shadow-sm" 
          @click="currentView = 'tokens'; fetchTokens();"
        >
          <i class="fas fa-list me-2"></i> View All Tokens ({{ activeTokensCount }})
        </button>
        <button 
          else 
          class="btn bg-gradient-info mb-0 shadow-sm" 
          @click="currentView = 'services'; fetchTokens();"
        >
          <i class="fas fa-th-large me-2"></i> Service Tiles Grid
        </button>

        <!-- KANBAN COUNTER BOARD TOGGLE (DRAG & DROP) - ACCESSIBLE FOR ROLES WITH COUNTER ACCESS INCLUDING DOCTORS -->
        <button 
          class="btn mb-0 shadow-sm"
          :class="currentView === 'board' ? 'bg-gradient-success text-white' : 'btn-outline-success'" 
          @click="currentView = 'board'; fetchTokens(); fetchCounters();"
        >
          <i class="fas fa-columns me-2"></i> Counter Board
          <span v-if="canDragDrop" class="badge bg-white text-success text-xxs ms-1">Drag & Drop</span>
        </button>
      </div>
    </div>

    <!-- ============================================================= -->
    <!-- VIEW 1: DEFAULT SERVICE TILES GRID VIEW                        -->
    <!-- ============================================================= -->
    <div v-if="currentView === 'services'" class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm border-0 bg-light">
          <div class="card-body px-4 pt-4">
            <div class="row">
              <div 
                v-for="item in servicesList" 
                :key="item.id" 
                class="col-xl-3 col-md-4 col-sm-6 mb-4"
              >
                <!-- Tile Card -->
                <div 
                  class="service-card shadow-sm" 
                  :style="getTileStyle(item)" 
                  :class="{ 'inactive-tile': !item.status }"
                  @click="onServiceTileClick(item)"
                >
                  <!-- Centered Body Content -->
                  <div class="tile-body d-flex flex-column align-items-center justify-content-center text-center py-4">
                    
                    <div class="icon-box-centered mb-3 d-flex align-items-center justify-content-center shadow">
                      <i :class="getServiceIcon(item.name)" class="text-white icon-size"></i>
                    </div>

                    <h5 class="text-white font-weight-bolder mb-0">{{ item.name }}</h5>
                    <span class="badge badge-sm bg-white text-dark mt-2 px-3">{{ item.prefix }}</span>
                    
                    <div class="mt-3">
                      <span v-if="item.status" class="status-dot bg-success"></span>
                      <span v-else class="status-dot bg-secondary"></span>
                      <small class="text-white text-xs ms-1">{{ item.status ? 'Active' : 'Inactive' }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="servicesList.length === 0" class="col-12 text-center py-5">
                <div class="text-secondary">
                   <i class="fas fa-folder-open mb-3 fa-3x opacity-4"></i>
                   <p>No services found. Please create services first in Service Management.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================= -->
    <!-- VIEW 2: ALL TOKENS TABLE & OPERATOR PANEL VIEW                -->
    <!-- ============================================================= -->
    <div v-if="currentView === 'tokens'">
      
      <!-- COUNTER OPERATOR PANEL (FIFO LOGIC) -->
      <div v-if="canAddToken && !showingExpired" class="row mb-4">
        <div class="col-12">
          <div class="card shadow-lg border-0" style="background: linear-gradient(87deg, #172b4d 0, #1a174d 100%) !important;">
            <div class="card-body p-4">
              <div class="row align-items-center">
                <div class="col-md-6 text-center text-md-start">
                  <h5 class="text-white mb-1">Operator Panel</h5>
                  <p class="text-white-50 text-xs mb-0">Call and process queue tokens in FIFO order</p>
                </div>
                <div class="col-md-6 d-flex justify-content-md-end justify-content-center gap-2 mt-3 mt-md-0">
                  
                  <!-- REFRESH BUTTON -->
                  <button class="btn btn-outline-light text-white btn-lg mb-0 shadow-sm" @click="fetchTokens(); checkServingStatus();">
                    <i class="fas fa-sync-alt me-2"></i> REFRESH
                  </button>

                  <!-- STATE: IDLE -->
                  <button v-if="!activeToken" class="btn btn-primary btn-lg mb-0 px-5 shadow-sm" @click="handleCallNextTrigger">
                    <i class="fas fa-bullhorn me-2"></i> CALL NEXT TOKEN
                  </button>

                  <!-- STATE: BUSY -->
                  <div v-else class="d-flex align-items-center bg-white-50 p-2 rounded gap-2" style="background: rgba(255,255,255,0.1);">
                    <div class="px-3 border-end border-white-50 text-start">
                      <span class="text-white text-xs text-uppercase opacity-8 d-block">Currently Serving</span>
                      <div class="d-flex align-items-center">
                        <span class="text-white fw-bold h5 mb-0">{{ activeToken.token_number }}</span>
                        <span v-if="isMedicalInstallation && activeToken.is_emergency" class="badge bg-danger ms-2 text-xxs">
                          <i class="fas fa-ambulance me-1"></i>EMERGENCY
                        </span>
                      </div>
                    </div>
                    <button class="btn btn-warning btn-sm mb-0" @click="handleCounterAction('moveToPending')">
                      <i class="fas fa-clock me-1"></i> PENDING DOCS
                    </button>
                    <button class="btn btn-secondary btn-sm mb-0" @click="handleCounterAction('markAsSkipped')">
                      <i class="fas fa-forward me-1"></i> SKIPPED
                    </button>
                    <button class="btn btn-success btn-sm mb-0" @click="handleCounterAction('completeAndNext')">
                      <i class="fas fa-check-circle me-1"></i> COMPLETE
                    </button>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Token Slider Overlay -->
      <div v-if="showSlider" class="token-slider-overlay" :style="dynamicThemeStyles">
        <button class="btn-back-slider" @click="closeSlider">
          <i class="fas fa-arrow-left me-2"></i> BACK
        </button>

        <div class="status-counters-wrapper">
          <div class="status-counter-item border-success-soft">
            <div class="sc-value text-success">{{ completedCount }}</div>
            <div class="sc-label">COMPLETED</div>
          </div>
          <div class="status-counter-item border-warning-soft">
            <div class="sc-value text-warning">{{ pendingCount }}</div>
            <div class="sc-label">WAITING</div>
          </div>
          <div class="status-counter-item border-secondary-soft">
            <div class="sc-value text-white">{{ skippedCount }}</div>
            <div class="sc-label">SKIPPED</div>
          </div>
          <div class="status-counter-item border-danger-soft">
            <div class="sc-value text-danger">{{ rejectedCount }}</div>
            <div class="sc-label">REJECTED</div>
          </div>
        </div>

        <button class="btn-close-slider" @click="closeSlider">
          <i class="fas fa-times"></i>
        </button>

        <div class="slider-wrapper" v-if="filteredTokens.length > 0">
          <button class="nav-btn prev" @click="prevSlide">
            <i class="fas fa-chevron-left"></i>
          </button>

          <div class="slider-main-content">
            <div class="row align-items-stretch justify-content-center w-100 g-4">
              <div class="col-lg-7 text-center d-flex flex-column justify-content-center border-end-divider">
                <transition name="slide-fade" mode="out-in">
                  <div :key="currentIndex" v-if="filteredTokens[currentIndex]">
                    <div class="d-flex justify-content-center gap-2 mb-3 flex-wrap">
                      <span class="badge px-4 py-2 shadow-lg theme-accent-bg">CURRENTLY SERVING</span>
                      <span v-if="isMedicalInstallation && filteredTokens[currentIndex].is_emergency" class="badge px-4 py-2 shadow-lg bg-danger text-uppercase">
                        <i class="fas fa-ambulance me-1"></i>EMERGENCY
                      </span>
                      <span :class="['badge px-4 py-2 shadow-lg text-uppercase', getStatusClass(filteredTokens[currentIndex].status)]">
                        {{ displayStatus(filteredTokens[currentIndex].status) }}
                      </span>
                    </div>

                    <div v-if="filteredTokens[currentIndex].token_attended && filteredTokens[currentIndex].token_attended !== 'No'" class="mb-2">
                      <h4 class="text-white opacity-9 text-uppercase ls-1 fw-bold mb-0">
                        <i class="fas fa-user-circle me-2 text-xs"></i>{{ filteredTokens[currentIndex].token_attended }}
                      </h4>
                    </div>

                    <h1 class="token-display-number">{{ filteredTokens[currentIndex].token_number }}</h1>
                    <p class="token-display-name">{{ filteredTokens[currentIndex].token_name }}</p>
                    <p class="text-white-50 text-uppercase ls-1 mt-n3">{{ filteredTokens[currentIndex].service?.name || '' }}</p>
                  </div>
                </transition>
              </div>

              <div class="col-lg-4 ms-lg-4 d-flex flex-column justify-content-between">
                <div class="side-info-card top-card mb-3 theme-card-bg">
                  <div class="card-label"><i class="fas fa-history me-2"></i>PREVIOUSLY CALLED</div>
                  <div class="d-flex align-items-center justify-content-between mt-2" v-if="filteredTokens.length > 1">
                    <h3 class="side-token-num">{{ prevTokenNumber }}</h3>
                    <p class="side-token-nm mb-0">{{ prevTokenName }}</p>
                  </div>
                </div>

                <transition name="fade-in" mode="out-in">
                  <div :key="nextTokenIndex" class="next-token-card theme-card-bg main-card-border">
                    <div class="next-header">
                      <i class="fas fa-arrow-right me-2 theme-accent-text"></i>
                      <span>COMING UP NEXT</span>
                    </div>
                    <div class="next-body">
                      <h2 class="next-token-num">{{ nextTokenNumber }}</h2>
                      <p class="next-token-nm">{{ nextTokenName }}</p>
                    </div>
                  </div>
                </transition>

                <div class="side-info-card bottom-card mt-3 theme-card-bg">
                  <div class="card-label"><i class="fas fa-info-circle me-2"></i>QUEUE INFORMATION</div>
                  <div class="mt-2 text-start">
                    <div class="d-flex justify-content-between text-white-50 text-xs text-uppercase mb-1">
                      <span>{{ showingExpired ? 'Total Historical' : 'Active Today' }}</span>
                      <span class="text-white fw-bold">{{ tokens.length }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button class="nav-btn next" @click="nextSlide">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- Token Table UI -->
      <div class="row">
        <div class="col-12">
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h6 class="font-weight-bolder mb-0">Token Queue List</h6>
                <small class="text-secondary">{{ selectedDate ? `Showing Tokens for ${selectedDate}` : (showingExpired ? 'Showing Historical Tokens' : 'Showing Active Tokens (Today & Yesterday)') }}</small>
              </div>
              <div class="d-flex flex-wrap align-items-center gap-2 w-100 w-md-auto justify-content-start justify-content-md-end">
                
                <!-- Search Input Filter -->
                <div class="position-relative me-2" style="min-width: 250px; max-width: 320px;">
                  <i class="fas fa-search position-absolute text-muted" style="left: 12px; top: 50%; transform: translateY(-50%); font-size: 13px;"></i>
                  <input
                    type="text"
                    v-model="tokenSearchQuery"
                    @input="onSearchInput"
                    class="form-control form-control-sm ps-4 pe-4 rounded-3 shadow-none border"
                    placeholder="Search token #, patient, phone..."
                  />
                  <button
                    v-if="tokenSearchQuery"
                    type="button"
                    class="btn btn-link position-absolute p-0 text-muted"
                    style="right: 10px; top: 50%; transform: translateY(-50%); text-decoration: none;"
                    @click="tokenSearchQuery = ''; onSearchInput()"
                  >
                    <i class="fas fa-times-circle"></i>
                  </button>
                </div>

                <!-- DATE PICKER: ACCESSIBLE FOR FILTERING ANY DAY'S TOKENS (WITH RESET TO TODAY) -->
                <div class="d-flex align-items-center gap-1 flex-grow-1 flex-md-grow-0">
                  <input 
                    type="date" 
                    class="form-control form-control-sm" 
                    v-model="selectedDate" 
                    @change="fetchTokens" 
                    title="Filter by Date"
                    style="min-width: 130px;"
                  >
                  <button 
                    v-if="selectedDate" 
                    class="btn btn-sm btn-link text-danger p-1 mb-0" 
                    @click="clearDateFilter" 
                    title="Reset to Today"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <div v-if="showingExpired || selectedDate" class="d-flex align-items-center gap-1 flex-grow-1 flex-md-grow-0">
                  <span class="text-xxs text-secondary font-weight-bold d-none d-lg-inline">TIME:</span>
                  <input 
                    type="time" 
                    class="form-control form-control-sm" 
                    v-model="selectedStartTime" 
                    title="From Time"
                    placeholder="From"
                    style="min-width: 95px;"
                  >
                  <span class="text-xs text-secondary">-</span>
                  <input 
                    type="time" 
                    class="form-control form-control-sm" 
                    v-model="selectedEndTime" 
                    title="To Time"
                    placeholder="To"
                    style="min-width: 95px;"
                  >
                  <button 
                    v-if="selectedStartTime || selectedEndTime" 
                    class="btn btn-sm btn-link text-danger p-1 mb-0" 
                    @click="clearTimeFilter" 
                    title="Clear Time Filter"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>

                <button class="btn btn-sm mb-0 shadow-sm px-3 flex-grow-1 flex-md-grow-0" :class="showingExpired ? 'btn-dark' : 'btn-outline-secondary'" @click="toggleExpired">
                  <i class="fas fa-history me-2"></i>{{ showingExpired ? 'Active Tokens' : 'Historical Tokens' }}
                </button>

                <!-- Export Buttons (Excel & PDF) -->
                <div class="btn-group shadow-sm flex-grow-1 flex-md-grow-0" role="group">
                  <button 
                    class="btn btn-sm btn-outline-success mb-0 px-2 px-md-3" 
                    @click="exportToExcel" 
                    title="Download Excel (.xlsx) Report"
                  >
                    <i class="fas fa-file-excel me-1 text-success"></i>Excel
                  </button>
                  <button 
                    class="btn btn-sm btn-outline-danger mb-0 px-2 px-md-3" 
                    @click="exportToPDF" 
                    title="Download PDF Report"
                  >
                    <i class="fas fa-file-pdf me-1 text-danger"></i>PDF
                  </button>
                </div>
                
                <button v-if="!showingExpired" class="btn btn-sm bg-gradient-info mb-0 shadow-sm px-3 flex-grow-1 flex-md-grow-0" @click="startSlider">
                  <i class="fas fa-play me-2"></i>View Token
                </button>
                
                <button v-if="!showingExpired && canAddToken" class="btn btn-sm bg-gradient-primary mb-0 shadow-sm px-3 flex-grow-1 flex-md-grow-0" @click="handleAction()">
                  <i class="fas fa-plus me-2"></i>{{ isDoctor ? 'Add New Patient' : 'Add Token' }}
                </button>
              </div>
            </div>
            <div class="card-body px-0 pt-0 pb-2">
              <div class="table-responsive p-0">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Sr. No.</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Service</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ isDoctor ? 'Patient Name' : 'User Name' }}</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Age / Gender</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Phone Number</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Token Number</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Counter</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Booking Schedule</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 d-none d-md-table-cell">Added / Updated At</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">{{ isDoctor ? 'Attended By (Doctor)' : 'Attended By' }}</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                      <th v-if="!showingExpired && canEditOrDelete" class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="(token, index) in filteredTokens" 
                      :key="token.id"
                      :draggable="canDragToken(token)"
                      @dragstart="onDragStart($event, token)"
                      :class="{ 
                        'draggable-row': canDragToken(token),
                        'yesterday-token-highlight': token.is_carryover || token.is_yesterday 
                      }"
                    >
                      <td class="ps-4">
                        <span v-if="canDragToken(token)" class="me-2 text-secondary opacity-6"><i class="fas fa-grip-vertical text-xxs"></i></span>
                        <p class="text-xs font-weight-bold mb-0 d-inline">{{ index + 1 }}</p>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0 text-primary">{{ token.service?.name || token.service_name || 'N/A' }}</p>
                        <small class="text-xxs text-secondary">{{ token.service?.prefix || token.prefix || '' }}</small>
                      </td>
                      <td>
                        <div class="d-flex align-items-center gap-1 flex-wrap">
                          <p class="text-xs font-weight-bold mb-0">{{ token.token_name || 'Walk-in' }}</p>
                          <span v-if="token.booking_type" class="badge text-xxs px-2 py-0" :class="getBookingTypeBadgeClass(token.booking_type)">
                            {{ token.booking_type }}
                          </span>
                        </div>
                        <small v-if="token.description" class="text-xxs text-secondary d-block text-truncate" style="max-width: 200px;" :title="token.description">
                          <i class="fas fa-notes-medical me-1 text-teal"></i>{{ token.description }}
                        </small>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          {{ token.age !== null && token.age !== undefined && token.age !== '' ? token.age : '-' }}
                          <span v-if="token.gender" class="text-secondary text-xxs ms-1">({{ token.gender }})</span>
                        </p>
                      </td>
                      <td><p class="text-xs font-weight-bold mb-0">{{ token.phone_number || '-' }}</p></td>
                      <td 
                        :style="String(token.status).toLowerCase() === 'calling' ? 'cursor: pointer;' : ''"
                        @click="handleCallingTokenClick(token)"
                      >
                        <div class="d-flex align-items-center flex-wrap gap-1">
                          <p class="text-xs font-weight-bold mb-0">{{ token.token_number }}</p>
                          <!-- YESTERDAY / CARRYOVER BADGE -->
                          <span
                            v-if="token.is_carryover || token.is_yesterday"
                            class="badge bg-warning-soft text-warning ms-1"
                            style="font-size: 10px; font-weight: 700; background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a;"
                            :title="token.carryover_label || 'Uncompleted token from yesterday'"
                          >
                            <i class="fas fa-history me-1"></i>{{ token.carryover_label || "Yesterday's Token" }}
                          </span>
                          <!-- EMERGENCY BADGE: DISPLAYED ONLY FOR MEDICAL INSTALLATION TYPE -->
                          <span v-if="isMedicalInstallation && token.is_emergency" class="badge bg-danger ms-2 text-xxs px-2 py-1">
                            <i class="fas fa-ambulance me-1"></i>EMERGENCY
                          </span>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex flex-column align-items-start">
                          <span v-if="getCounterName(token)" class="badge bg-gradient-light text-dark font-weight-bold">
                            {{ getCounterName(token) }}
                          </span>
                          <span v-else class="text-xs text-secondary">-</span>

                          <!-- Badge showing how many counters visited -->
                          <span 
                            v-if="(token.history && token.history.length > 1) || (token.visit_count && token.visit_count > 1) || (token.visited_counters && token.visited_counters.length > 1)" 
                            class="badge bg-info-subtle text-info mt-1 cursor-pointer"
                            style="cursor: pointer; font-size: 11px;"
                            @click="openTokenHistory(token)"
                            title="Click to view visited counters"
                          >
                            <i class="fas fa-route me-1"></i> {{ (token.history && token.history.length) || token.visit_count || (token.visited_counters ? token.visited_counters.length : 1) }} Counters Visited
                          </span>
                        </div>
                      </td>
                      <td>
                        <div v-if="token.booking_date || token.booking_time">
                          <p class="text-xs font-weight-bold mb-0 text-dark">
                            <i class="far fa-calendar-alt text-primary me-1"></i>{{ token.booking_date || '-' }}
                          </p>
                          <small class="text-xxs text-secondary font-weight-bold" v-if="token.booking_time">
                            <i class="far fa-clock text-info me-1"></i>{{ token.booking_time }}
                          </small>
                        </div>
                        <span v-else class="text-xs text-secondary">-</span>
                      </td>
                      <td class="d-none d-md-table-cell">
                        <p class="text-xs font-weight-bold mb-0">
                          {{ formatDateTime(token.updatedAt || token.updated_at || token.createdAt || token.created_at) }}
                        </p>
                        <small v-if="(token.updatedAt || token.updated_at) && formatDateTime(token.updatedAt || token.updated_at) !== formatDateTime(token.createdAt || token.created_at)" class="text-xxs text-secondary d-block">
                          Created: {{ formatDateTime(token.createdAt || token.created_at) }}
                        </small>
                      </td>
                      <td>
                        <span :class="token.token_attended && token.token_attended !== 'No' ? 'badge bg-gradient-success' : 'badge bg-gradient-secondary'">
                          {{ token.token_attended && token.token_attended !== 'No' ? token.token_attended : (token.user?.name || token.attendant_name || 'No') }}
                        </span>
                      </td>
                      <td 
                        :style="String(token.status).toLowerCase() === 'calling' ? 'cursor: pointer;' : ''"
                        @click="handleCallingTokenClick(token)"
                      >
                        <span :class="getStatusClass(token.status)" class="badge text-uppercase" style="font-size: 0.65rem;">
                          {{ displayStatus(token.status) }}
                        </span>
                      </td>
                      <td v-if="!showingExpired && canEditOrDelete" class="text-center">
                        <div class="d-flex justify-content-center align-items-center">
                          <button 
                            class="btn btn-link text-info px-2 mb-0" 
                            @click="openTokenHistory(token)"
                            title="View Counter History"
                          >
                            <i class="fas fa-history"></i>
                          </button>
                          <button v-if="canUpdateToken" class="btn btn-link text-primary px-2 mb-0" @click="handleAction(token)"><i class="fas fa-edit"></i></button>
                          <button v-if="canDeleteToken" class="btn btn-link text-danger px-2 mb-0" @click="deleteToken(token.id)"><i class="fas fa-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="filteredTokens.length === 0">
                      <td colspan="12" class="text-center py-4 text-secondary">
                        <div class="py-3">
                          <i class="fas fa-ticket-alt fa-2x mb-2 text-secondary opacity-5"></i>
                          <p class="mb-1 font-weight-bold">No tokens found {{ searchQuery ? 'matching search' : (selectedDate ? 'for ' + selectedDate : '') }}{{ selectedStartTime || selectedEndTime ? ' at selected timing' : '' }}</p>
                          <small v-if="!showingExpired" class="text-xs text-muted d-block">
                            Looking for tokens created on previous dates? Click 
                            <a href="javascript:;" class="text-primary font-weight-bold text-decoration-underline" @click="toggleExpired">Historical Tokens</a> to view them.
                          </small>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================= -->
    <!-- VIEW 3: COUNTER BOARD (DRAG & DROP KANBAN)                    -->
    <!-- ============================================================= -->
    <div v-if="currentView === 'board'" class="row">
      <div class="col-12">

        <!-- BOARD COLUMNS CONTAINER -->
        <div class="counter-board-wrapper pb-3">
          
          <!-- COLUMN 0: UNASSIGNED / WAITING QUEUE -->
          <div 
            class="counter-column"
            :class="{ 'drop-active': dragOverColumnId === 'unassigned' }"
            @dragover.prevent="onDragOver('unassigned')"
            @dragleave="onDragLeave"
            @drop="onDrop('unassigned')"
          >
            <div class="counter-column-header bg-gradient-secondary text-white p-3 rounded-top">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-white font-weight-bold"><i class="fas fa-users me-2"></i>Unassigned Queue</h6>
                <span class="badge bg-white text-dark">{{ getTokensForCounter(null).length }}</span>
              </div>
              <small class="text-white-50 text-xxs">General waiting pool</small>
            </div>

            <div class="counter-column-body p-2">
              <div 
                v-for="tok in getTokensForCounter(null)" 
                :key="tok.id"
                class="token-drag-card p-3 mb-2 shadow-sm rounded bg-white border"
                :draggable="canDragToken(tok)"
                @dragstart="onDragStart($event, tok)"
                :class="{ 'draggable-row': canDragToken(tok) }"
                :style="!canDragToken(tok) ? 'cursor: default;' : ''"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span :class="isMedicalInstallation && tok.is_emergency ? 'badge bg-danger text-white fw-bold' : 'badge bg-light text-dark fw-bold'">
                      {{ tok.token_number }}
                    </span>
                    <span v-if="isMedicalInstallation && tok.is_emergency" class="badge bg-danger text-xxs ms-1">
                      <i class="fas fa-ambulance me-1"></i>EMERGENCY
                    </span>
                  </div>
                  <span :class="getStatusClass(tok.status)" class="badge text-uppercase text-xxs">{{ displayStatus(tok.status) }}</span>
                </div>
                <h6 class="text-sm font-weight-bold mb-0 text-dark">{{ tok.token_name || 'Walk-in' }}</h6>
                <div class="d-flex justify-content-between align-items-center mt-2 text-xxs text-secondary">
                  <span>{{ tok.service?.name || 'Service' }}</span>
                  <i v-if="canDragToken(tok)" class="fas fa-grip-vertical text-muted"></i>
                </div>
              </div>

              <div v-if="getTokensForCounter(null).length === 0" class="text-center py-4 text-secondary text-xs italic">
                No unassigned tokens
              </div>
            </div>
          </div>

          <!-- DYNAMIC COUNTER COLUMNS -->
          <div 
            v-for="counter in countersList" 
            :key="counter.id"
            class="counter-column"
            :class="{ 'drop-active': dragOverColumnId === counter.id }"
            @dragover.prevent="onDragOver(counter.id)"
            @dragleave="onDragLeave"
            @drop="onDrop(counter)"
          >
            <div class="counter-column-header bg-gradient-dark text-white p-3 rounded-top">
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0 text-white font-weight-bold text-truncate" style="max-width: 170px;">
                  <i class="fas fa-desktop me-2"></i>{{ counter.name }}
                </h6>
                <span class="badge bg-white text-dark">{{ getTokensForCounter(counter).length }}</span>
              </div>
              <div class="text-white-50 text-xxs text-truncate mt-1">
                <i class="fas fa-user-circle me-1"></i>{{ counter.user?.name || counter.attendant_name || 'Staff' }}
              </div>
            </div>

            <div class="counter-column-body p-2">
              <div 
                v-for="tok in getTokensForCounter(counter)" 
                :key="tok.id"
                class="token-drag-card p-3 mb-2 shadow-sm rounded bg-white border"
                :draggable="canDragToken(tok)"
                @dragstart="onDragStart($event, tok)"
                :class="{ 'draggable-row': canDragToken(tok) }"
                :style="!canDragToken(tok) ? 'cursor: default;' : ''"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center">
                    <span :class="isMedicalInstallation && tok.is_emergency ? 'badge bg-danger text-white fw-bold' : 'badge bg-primary text-white fw-bold'">
                      {{ tok.token_number }}
                    </span>
                    <span v-if="isMedicalInstallation && tok.is_emergency" class="badge bg-danger text-xxs ms-1">
                      <i class="fas fa-ambulance me-1"></i>EMERGENCY
                    </span>
                  </div>
                  <span 
                    :class="getStatusClass(tok.status)" 
                    class="badge text-uppercase text-xxs"
                    :style="String(tok.status).toLowerCase() === 'calling' ? 'cursor: pointer;' : ''"
                    @click.stop="handleCallingTokenClick(tok)"
                  >{{ displayStatus(tok.status) }}</span>
                </div>
                <h6 class="text-sm font-weight-bold mb-0 text-dark">{{ tok.token_name || 'Walk-in' }}</h6>
                <div class="d-flex justify-content-between align-items-center mt-2 text-xxs text-secondary">
                  <span>{{ tok.service?.name || 'Service' }}</span>
                  <i v-if="canDragToken(tok)" class="fas fa-grip-vertical text-muted"></i>
                </div>
              </div>

              <div v-if="getTokensForCounter(counter).length === 0" class="text-center py-4 text-secondary text-xs italic">
                Drop token here
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- TOKEN JOURNEY & COUNTER HISTORY MODAL -->
    <div 
      v-if="showHistoryModal" 
      class="modal fade show d-block" 
      style="background: rgba(0,0,0,0.5); z-index: 1055;"
      tabindex="-1"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content shadow-lg border-0" style="border-radius: 12px;">
          
          <!-- Modal Header -->
          <div class="modal-header bg-dark text-white" style="border-radius: 12px 12px 0 0;">
            <div class="d-flex align-items-center">
              <i class="fas fa-history me-2 text-primary"></i>
              <div>
                <h5 class="modal-title mb-0 text-white">Token Journey & Counter History</h5>
                <small class="text-white-50">
                  Token: <strong>{{ selectedToken ? selectedToken.token_number : '' }}</strong> | 
                  Patient: <strong>{{ selectedToken ? (selectedToken.token_name || 'Walk-in') : '' }}</strong>
                </small>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-white" @click="closeTokenHistory"></button>
          </div>
          <!-- Modal Body -->
          <div class="modal-body p-4">
            <!-- Summary Counter Stats -->
            <div class="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
              <div>
                <span class="text-muted d-block small">Total Visits / Transfers</span>
                <span class="fs-5 fw-bold text-primary">
                  {{ (tokenHistoryList && tokenHistoryList.length > 0) ? tokenHistoryList.length : (selectedToken ? ((selectedToken.history && selectedToken.history.length) || selectedToken.visit_count || (selectedToken.visited_counters ? selectedToken.visited_counters.length : 1)) : 1) }}
                </span>
              </div>
              <div>
                <span class="text-muted d-block small">Current Status</span>
                <span class="badge bg-warning text-dark text-uppercase">
                  {{ selectedToken ? displayStatus(selectedToken.status) : '' }}
                </span>
              </div>
              <div>
                <span class="text-muted d-block small">Current Counter</span>
                <span class="fw-bold">
                  {{ selectedToken ? (getCounterName(selectedToken) || selectedToken.counter_name || 'N/A') : 'N/A' }}
                </span>
              </div>
            </div>
            <!-- Timeline of Visited Counters -->
            <h6 class="fw-bold mb-3 text-secondary">
              <i class="fas fa-map-signs me-1"></i> Counter Visit Flow:
            </h6>
            <div v-if="!tokenHistoryList || tokenHistoryList.length === 0" class="text-center text-muted py-4">
              No previous transfers. Currently at initial counter.
            </div>
            <div class="timeline" v-else>
              <div 
                v-for="(item, index) in tokenHistoryList" 
                :key="item.id || index"
                class="card mb-3 border-start border-4 shadow-sm"
                :class="item.status === 'completed' ? 'border-success' : (item.status === 'serving' ? 'border-primary' : 'border-warning')"
              >
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <span class="badge rounded-pill bg-dark me-2">#{{ index + 1 }}</span>
                      <h6 class="mb-0 fw-bold">
                        {{ item.counter_name || (item.counter ? item.counter.name : 'Unassigned / Waiting Area') }}
                      </h6>
                    </div>
                    <span 
                      class="badge text-uppercase"
                      :class="{
                        'bg-success': item.status === 'completed',
                        'bg-primary': item.status === 'serving',
                        'bg-warning text-dark': item.status === 'pending' || item.status === 'waiting',
                        'bg-secondary': item.status === 'skipped'
                      }"
                    >
                      {{ displayStatus(item.status) }}
                    </span>
                  </div>
                  <div class="row mt-2 text-muted small">
                    <div class="col-md-6">
                      <i class="fas fa-user-md me-1 text-primary"></i>
                      <strong>Attended By:</strong> 
                      <span class="text-dark">{{ item.attendant_name || (item.user ? item.user.name : 'Not attended yet') }}</span>
                    </div>
                    <div class="col-md-6" v-if="item.service_name || (item.service && item.service.name)">
                      <i class="fas fa-stethoscope me-1 text-info"></i>
                      <strong>Service:</strong> {{ item.service_name || item.service.name }}
                    </div>
                    <div class="col-md-6 mt-1" v-if="item.start_time || item.createdAt || item.created_at">
                      <i class="far fa-clock me-1 text-secondary"></i>
                      <strong>Time:</strong> {{ item.start_time || formatDateTime(item.createdAt || item.created_at) }}
                    </div>
                    <div class="col-md-6 mt-1" v-if="item.duration_seconds">
                      <i class="fas fa-hourglass-half me-1 text-warning"></i>
                      <strong>Duration:</strong> {{ Math.round(item.duration_seconds / 60) }} mins
                    </div>
                    <div class="col-12 mt-1" v-if="item.notes || item.action">
                      <i class="fas fa-info-circle me-1 text-muted"></i>
                      <strong>Action / Note:</strong> {{ item.notes || item.action }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Modal Footer -->
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeTokenHistory">Close</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "Tokens",
  data() {
    return {
      currentView: 'services', // DEFAULT VIEW IS 'services' GRID (SWITCHABLE TO 'tokens' or 'board')
      tokens: [],
      servicesList: [],
      countersList: [],
      attendants: [],
      activeToken: null,
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
      showSlider: false,
      currentIndex: 0,
      showingExpired: false,
      selectedDate: "",
      selectedStartTime: "",
      selectedEndTime: "",
      userRole: "",
      searchQuery: "",
      tokenSearchQuery: "",
      activeTheme: localStorage.getItem('themeColor') || '#1e3a5f',
      draggedToken: null,
      dragOverColumnId: null,
      showHistoryModal: false,
      selectedToken: null,
      tokenHistoryList: []
    };
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/\s+/g, '');
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      return role.includes('superadmin') || user.type === 1 || user.role === 'SuperAdmin' || user.role_name === 'SuperAdmin';
    },

    // Strictly identify whether the logged-in user is an Admin or Super Admin
    isAdminOrSuperAdmin() {
      if (this.isSuperAdmin) return true;
      if (this.isDoctor) return false;
      const role = String(this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      const userType = Number(user.type || localStorage.getItem('userType') || 0);

      // Non-admin roles must NEVER be treated as Admin
      const nonAdminRoles = ['doctor', 'operator', 'tokenuser', 'receptionist', 'nurse', 'teller', 'attendant'];
      if (nonAdminRoles.some(r => role.includes(r))) return false;

      // Only true SuperAdmin or Admin
      return role === 'admin' || 
             role === 'superadmin' || 
             role === 'tenantadmin' || 
             role.includes('superadmin') || 
             (role.includes('admin') && !nonAdminRoles.some(r => role.includes(r))) || 
             userType === 1;
    },

    // Unrestricted role check: Admin, SuperAdmin, TokenManager, and Receptionist can drag any token to any counter
    isUnrestrictedManager() {
      const role = String(this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      return this.isSuperAdmin || 
             ['admin', 'superadmin', 'tokenmanager', 'receptionist'].some(r => role.includes(r));
    },

    // MEDICAL INSTALLATION TYPE GUARD: Checks if installation type is Medical / Hospital / Clinic
    isMedicalInstallation() {
      const rawType = (
        localStorage.getItem('installation_type') ||
        localStorage.getItem('installationType') ||
        localStorage.getItem('installation_type_id') ||
        localStorage.getItem('installationTypeId') ||
        localStorage.getItem('installation_type_slug') ||
        ''
      ).toString().toLowerCase().trim();

      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }

      const userInstallType = (
        user.installation_type ||
        user.installationType ||
        user.installation_type_id ||
        user.installationTypeId ||
        user.installation_type_slug ||
        ''
      ).toString().toLowerCase().trim();

      const configType = (
        localStorage.getItem('app_type') || 
        localStorage.getItem('system_type') || 
        localStorage.getItem('app_installation_type') || 
        ''
      ).toString().toLowerCase().trim();

      // True if installation type contains medical, hospital, clinic, or matches medical ID / doctor role
      const isMed = 
        rawType.includes('medical') || rawType.includes('hospital') || rawType.includes('clinic') || rawType === '1' || rawType === '2' ||
        userInstallType.includes('medical') || userInstallType.includes('hospital') || userInstallType.includes('clinic') || userInstallType === '1' || userInstallType === '2' ||
        configType.includes('medical') || configType.includes('hospital') ||
        this.isDoctor;

      return Boolean(isMed);
    },

    // DRAG AND DROP ROLE GUARD (Permits Admin, SuperAdmin, TokenManager, Receptionist, TokenUser, Operator, and Doctors)
    canDragDrop() {
      const role = String(this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      return this.isUnrestrictedManager ||
             ['admin', 'superadmin', 'tokenmanager', 'receptionist', 'tokenuser', 'operator', 'doctor'].includes(role) || 
             role.includes('tokenuser') || 
             role.includes('operator') || 
             role.includes('doctor') || 
             role.includes('receptionist') || 
             this.isDoctor;
    },
    activeTokensCount() {
      if (this.showingExpired) return this.filteredTokens.length;
      return this.filteredTokens.filter(t => String(t.status || '').toLowerCase() !== 'completed').length;
    },
    filteredTokens() {
      let list = this.tokens;

      // Filter out completed records from the active queue list display unless a specific date/expired view is requested
      if (!this.showingExpired && !this.selectedDate) {
        list = list.filter(t => String(t.status || '').toLowerCase() !== 'completed');
      }

      // =========================================================================
      // DATE FILTERING LOGIC:
      // - If a specific date is selected in the date picker: show tokens for that booking date
      // - If no date is selected on active view: show ONLY Today's and Yesterday's tokens
      // - Older prior dates (October 30, 29, 28, etc.) are excluded unless their date is selected
      // =========================================================================
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;

      const yDate = new Date(now);
      yDate.setDate(yDate.getDate() - 1);
      const yYear = yDate.getFullYear();
      const yMonth = String(yDate.getMonth() + 1).padStart(2, '0');
      const yDay = String(yDate.getDate()).padStart(2, '0');
      const yesterdayStr = `${yYear}-${yMonth}-${yDay}`;

      const extractDateStr = (t) => {
        let raw = t.booking_date || t.bookingDate || t.date || t.token_date;
        if (!raw && (t.createdAt || t.created_at)) raw = t.createdAt || t.created_at;
        if (!raw) return '';
        const s = String(raw).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          return s.slice(0, 10);
        }
        const ddmmyyyy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
        if (ddmmyyyy) {
          return `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}`;
        }
        try {
          const d = new Date(s);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const dt = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${dt}`;
          }
        } catch (e) {
          // ignore
        }
        return s.split('T')[0].trim();
      };

      if (this.selectedDate) {
        list = list.filter(t => {
          const tDate = extractDateStr(t);
          return tDate === this.selectedDate;
        });
      } else if (!this.showingExpired) {
        // Active tokens: show ONLY Today's and Yesterday's tokens
        list = list.filter(t => {
          const tDate = extractDateStr(t);
          const isToday = tDate === todayStr;
          const isYesterday = tDate === yesterdayStr || 
                              t.is_yesterday === true || 
                              String(t.carryover_label || '').toLowerCase().includes('yesterday');
          return isToday || isYesterday;
        });
      }

      // =========================================================================
      // VISIBILITY LOGIC:
      // Admins, Super Admins, Token Managers, Receptionists, Operators, Tellers, Nurses
      // have full visibility to manage queue tokens.
      // Doctors see their assigned tokens, emergency tokens, and waiting/unassigned
      // queue tokens for their counter/services so they can call next.
      // =========================================================================
      const cleanedRole = String(this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      const isQueueManager = this.isAdminOrSuperAdmin || ['tokenmanager', 'tokenmanger', 'receptionist', 'reception', 'tokenuser', 'operator', 'teller', 'staff', 'nurse', '1', '2'].some(r => cleanedRole.includes(r));

      if (!isQueueManager) {
        const currentUserId = String(localStorage.getItem('id') || localStorage.getItem('userId') || '');
        const currentUserName = String(localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase().trim();
        const cleanDocName = currentUserName.replace(/^dr\.?\s*/i, '').trim();

        const storedCounterId = localStorage.getItem('counter_id');
        const storedRoom = (localStorage.getItem('doctorRoom') || localStorage.getItem('counter_name') || '').toLowerCase().replace(/^room\s*/i, '').trim();

        // 1. Identify all counters linked to this logged-in user / doctor
        const myCounters = (this.countersList || []).filter(c => {
          const cUserId = String(c.user_id || (c.user ? c.user.id : '') || '');
          const cAttName = String(c.user?.name || c.attendant_name || '').toLowerCase().trim();
          const cleanAttName = cAttName.replace(/^dr\.?\s*/i, '').trim();
          const cRoom = String(c.name || '').toLowerCase().replace(/^room\s*/i, '').trim();
          const matchesUser = currentUserId && cUserId === currentUserId;
          const matchesAttendant = currentUserName && (cAttName === currentUserName || (cleanDocName && cleanAttName.includes(cleanDocName)));
          const matchesRoom = storedRoom && cRoom && (storedRoom === cRoom || storedRoom.includes(cRoom) || cRoom.includes(storedRoom));
          return matchesUser || matchesAttendant || matchesRoom;
        });

        const myCounterIds = myCounters.map(c => String(c.id));
        if (storedCounterId && !myCounterIds.includes(String(storedCounterId))) {
          myCounterIds.push(String(storedCounterId));
        }

        // Collect doctor's counter service IDs
        const myServiceIds = new Set();
        myCounters.forEach(c => {
          let sIds = c.service_ids;
          while (typeof sIds === 'string') {
            try { sIds = JSON.parse(sIds); } catch(e) { sIds = sIds.split(',').map(s => s.trim()); break; }
          }
          if (Array.isArray(sIds)) sIds.forEach(id => myServiceIds.add(Number(id)));
        });

        list = list.filter(t => {
          // EMERGENCY TOKENS: ALWAYS VISIBLE TO EVERYONE REGARDLESS OF ROLE
          const isEm = t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true' || String(t.is_emergency || '').toLowerCase() === 'yes';
          if (isEm) return true;

          const uId = String(t.user_id || '');
          const cId = String(t.counter_id || '');
          const tAttName = String(t.token_attended || '').toLowerCase().trim();
          const cleanTAtt = tAttName.replace(/^dr\.?\s*/i, '').trim();
          const tRoom = String(t.counter?.name || t.counter_name || t.room || '').toLowerCase().replace(/^room\s*/i, '').trim();

          const matchesUser = currentUserId && uId === currentUserId;
          const matchesCounter = cId && myCounterIds.includes(cId);
          const matchesAttendant = currentUserName && tAttName !== 'no' && (tAttName === currentUserName || (cleanDocName && cleanTAtt.includes(cleanDocName)));
          const matchesRoom = storedRoom && tRoom && (storedRoom === tRoom || storedRoom.includes(tRoom) || tRoom.includes(storedRoom));

          if (matchesUser || matchesCounter || matchesAttendant || matchesRoom) return true;

          // Allow pending / waiting tokens (newly added by Admin or kiosk)
          const st = String(t.status || '').toLowerCase();
          const isPending = st === 'pending' || st === 'waiting';
          if (isPending) {
            const isUnassigned = !uId || uId === '0' || !tAttName || tAttName === 'no';
            const tServiceId = Number(t.service_id || (t.service ? t.service.id : 0));
            if (tServiceId && myServiceIds.has(tServiceId)) return true;
            if (isUnassigned && (!cId || cId === '0' || myCounterIds.includes(cId))) return true;
          }

          return false;
        });
      }

      // Filter by Time (applicable for Historical Tokens or whenever timing filter is set)
      if (this.selectedStartTime || this.selectedEndTime) {
        list = list.filter(t => {
          let tTime = "";
          if (t.booking_time) {
            const bt = String(t.booking_time).trim();
            const timeMatch = bt.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
            if (timeMatch) {
              let h = parseInt(timeMatch[1], 10);
              const m = timeMatch[2];
              const ampm = timeMatch[3] ? timeMatch[3].toUpperCase() : null;
              if (ampm === 'PM' && h < 12) h += 12;
              if (ampm === 'AM' && h === 12) h = 0;
              tTime = `${String(h).padStart(2, '0')}:${m}`;
            }
          }
          if (!tTime) {
            const rawDate = t.createdAt || t.created_at || t.token_time || t.added_at;
            if (!rawDate) return false;
            let d = new Date(rawDate);
            if (isNaN(d.getTime())) {
              d = new Date(rawDate.replace(' ', 'T'));
            }
            if (isNaN(d.getTime())) return false;
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            tTime = `${hh}:${mm}`;
          }

          const start = this.selectedStartTime;
          const end = this.selectedEndTime;

          if (start && end) {
            return start <= end 
              ? (tTime >= start && tTime <= end)
              : (tTime >= start || tTime <= end);
          }
          if (start) return tTime >= start;
          if (end) return tTime <= end;
          return true;
        });
      }

      const rawSearch = (this.tokenSearchQuery || this.searchQuery || '').toLowerCase().trim();
      if (!rawSearch) return list;
      return list.filter(t => {
        const num = String(t.token_number || '').toLowerCase();
        const name = String(t.token_name || t.user_name || '').toLowerCase();
        const phone = String(t.phone_number || '').toLowerCase();
        const srv = String(t.service?.name || t.service_name || '').toLowerCase();
        const counter = String(t.counter?.name || t.counter_name || '').toLowerCase();
        const att = String(t.token_attended || t.attended_by || '').toLowerCase();
        const desc = String(t.description || '').toLowerCase();
        return num.includes(rawSearch) || 
               name.includes(rawSearch) || 
               phone.includes(rawSearch) || 
               srv.includes(rawSearch) || 
               counter.includes(rawSearch) || 
               att.includes(rawSearch) ||
               desc.includes(rawSearch);
      });
    },
    filteredTokensList() {
      return this.filteredTokens;
    },
    canAddToken() {
      return this.checkPermission('create');
    },
    canUpdateToken() {
      return this.checkPermission('update');
    },
    canDeleteToken() {
      return this.checkPermission('delete');
    },
    canEditOrDelete() {
      return this.canUpdateToken || this.canDeleteToken;
    },
    dynamicThemeStyles() {
      const primaryColor = localStorage.getItem('themeColor') || '#5e72e4'; 
      const sidebarBg = localStorage.getItem('sidebarColor') || '#1a1a2e'; 
      return {
        '--theme-primary': primaryColor,
        '--theme-bg': sidebarBg,
        '--theme-card': 'rgba(255, 255, 255, 0.05)'
      };
    },
    isDoctor() {
      const rawRole = this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '';
      let user = {};
      try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch(e) { user = {}; }
      const userRole = user.role?.name || user.role || user.role_name || '';
      const userName = String(user.name || localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase().trim();
      const combined = `${rawRole} ${userRole}`.toLowerCase().replace(/[^a-z0-9]/gi, '');
      const doctorKeywords = ['doctor', 'physician', 'consultant', 'specialist', 'surgeon', 'opd'];
      return doctorKeywords.some(k => combined.includes(k)) || userName.startsWith('dr.') || userName.startsWith('dr ');
    },
    filteredAttendantsList() {
      if (!Array.isArray(this.attendants)) return [];

      const currentRole = String(this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');

      const getRole = (a) => {
        if (!a) return '';
        let r = '';
        if (a.role_name) r = String(a.role_name);
        else if (a.role_slug) r = String(a.role_slug);
        else if (a.roleName) r = String(a.roleName);
        else if (a.roleSlug) r = String(a.roleSlug);
        else if (a.user_role) r = String(a.user_role);
        else if (a.userRole) r = String(a.userRole);
        else if (a.role_type) r = String(a.role_type);
        else if (a.role) {
          if (typeof a.role === 'string') r = a.role;
          else if (typeof a.role === 'object') {
            r = a.role.name || a.role.role_name || a.role.slug || a.role.title || '';
          }
        } else if (a.Role) {
          if (typeof a.Role === 'string') r = a.Role;
          else if (typeof a.Role === 'object') {
            r = a.Role.name || a.Role.role_name || a.Role.slug || a.Role.title || '';
          }
        }
        return String(r).toLowerCase().replace(/[^a-z0-9]/gi, '');
      };

      // 1. DOCTOR ROLE FILTER: Show ONLY doctors in Attended By field
      if (currentRole.includes('doctor')) {
        let docs = this.attendants.filter(a => {
          const r = getRole(a);
          const name = String(a.name || '').toLowerCase();
          return r.includes('doctor') || name.startsWith('dr') || name.includes('doctor');
        });

        const currentName = localStorage.getItem('userName') || localStorage.getItem('name');
        const currentId = localStorage.getItem('id');
        if (currentName) {
          const exists = docs.some(d => String(d.name).toLowerCase() === String(currentName).toLowerCase() || (currentId && d.id == currentId));
          if (!exists) {
            docs.unshift({ id: currentId || '', name: currentName, role_name: 'doctor' });
          }
        }

        return docs;
      }

      // 2. TOKENUSER & TOKENMANAGER ROLE FILTER: Show ONLY tokenuser and tokenmanager staff (exclude Doctors)
      if (currentRole.includes('tokenuser') || currentRole.includes('tokenmanager') || currentRole.includes('tokenmanger')) {
        let tokenStaff = this.attendants.filter(a => {
          const r = getRole(a);
          const name = String(a.name || '').toLowerCase();
          const isDoc = r.includes('doctor') || name.startsWith('dr') || name.includes('doctor');
          if (isDoc) return false;
          return r.includes('tokenuser') || r.includes('tokenmanager') || r.includes('tokenmanger') || r.includes('operator') || r.includes('staff') || r === 'user' || r === '';
        });

        return tokenStaff;
      }

      // 3. ADMIN / SUPERADMIN / OTHER ROLES
      if (this.isDoctor) {
        return this.attendants.filter(a => {
          const r = getRole(a);
          const name = String(a.name || '').toLowerCase();
          return r.includes('doctor') || name.startsWith('dr') || name.includes('doctor');
        });
      }

      return this.attendants;
    },
    completedCount() { return this.filteredTokens.filter(t => String(t.status || '').toLowerCase() === 'completed').length; },
    pendingCount() { return this.filteredTokens.filter(t => String(t.status || '').toLowerCase() === 'pending').length; },
    skippedCount() { return this.filteredTokens.filter(t => String(t.status || '').toLowerCase() === 'skipped').length; },
    rejectedCount() { return this.filteredTokens.filter(t => String(t.status || '').toLowerCase() === 'rejected').length; },

    prevTokenIndex() {
      if (this.filteredTokens.length === 0) return 0;
      return (this.currentIndex - 1 + this.filteredTokens.length) % this.filteredTokens.length;
    },
    prevTokenName() { return this.filteredTokens[this.prevTokenIndex]?.token_name || ''; },
    prevTokenNumber() { return this.filteredTokens[this.prevTokenIndex]?.token_number || ''; },
    nextTokenIndex() {
      if (this.filteredTokens.length === 0) return 0;
      return (this.currentIndex + 1) % this.filteredTokens.length;
    },
    nextTokenName() { return this.filteredTokens[this.nextTokenIndex]?.token_name || ''; },
    nextTokenNumber() { return this.filteredTokens[this.nextTokenIndex]?.token_number || ''; }
  },
  methods: {
    // Check if a token belongs strictly to the logged-in doctor or their assigned counter
    isMyToken(token) {
      if (!token) return false;
      const currentUserId = String(localStorage.getItem('id') || localStorage.getItem('userId') || '');
      const currentUserName = String(localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase().trim();
      const cleanDocName = currentUserName.replace(/^dr\.?\s*/i, '').trim();

      // Check User ID match
      if (currentUserId && String(token.user_id) === currentUserId) return true;

      // Check Attendant Name match
      const tAttName = String(token.token_attended || '').toLowerCase().trim();
      if (tAttName && tAttName !== 'no') {
        if (currentUserName && tAttName === currentUserName) return true;
        if (cleanDocName && tAttName.includes(cleanDocName)) return true;
      }

      // Check Counter ID match
      if (token.counter_id && Array.isArray(this.countersList)) {
        const isDocCounter = this.countersList.some(c => {
          if (String(c.id) !== String(token.counter_id)) return false;
          const cUserId = String(c.user_id || (c.user ? c.user.id : '') || '');
          const cAttName = String(c.user?.name || c.attendant_name || '').toLowerCase().trim();
          const matchesUser = currentUserId && cUserId === currentUserId;
          const matchesAttendant = currentUserName && (cAttName === currentUserName || (cleanDocName && cAttName.includes(cleanDocName)));
          return matchesUser || matchesAttendant;
        });
        if (isDocCounter) return true;
      }

      // Check Service match for doctor's counter
      if (Array.isArray(this.countersList)) {
        const tServiceId = Number(token.service_id || (token.service ? token.service.id : 0));
        const isUnassigned = !token.user_id || token.user_id === '0' || tAttName === 'no' || !token.token_attended;
        if (isUnassigned && tServiceId > 0) {
          const matchesMyCounterService = this.countersList.some(c => {
            const cUserId = String(c.user_id || (c.user ? c.user.id : '') || '');
            const cAttName = String(c.user?.name || c.attendant_name || '').toLowerCase().trim();
            const matchesUser = currentUserId && cUserId === currentUserId;
            const matchesAttendant = currentUserName && (cAttName === currentUserName || (cleanDocName && cAttName.includes(cleanDocName)));
            if (!matchesUser && !matchesAttendant) return false;

            let sIds = c.service_ids;
            while (typeof sIds === 'string') {
              try {
                const parsed = JSON.parse(sIds);
                if (parsed === sIds) break;
                sIds = parsed;
              } catch (e) {
                sIds = sIds.split(',').map(s => s.trim());
                break;
              }
            }
            return Array.isArray(sIds) && sIds.map(Number).includes(tServiceId);
          });
          if (matchesMyCounterService) return true;
        }
      }

      return false;
    },

    // Determines if a specific token card can be dragged by the current user
    // Unrestricted roles (SuperAdmin, Admin, TokenManager, Receptionist) can drag ANY token
    canDragToken(token) {
      if (!this.canDragDrop) return false;
      if (this.isSuperAdmin || this.isUnrestrictedManager) return true;

      // DOCTOR RESTRICTION: Doctor can ONLY drag tokens assigned to their counter
      if (this.isDoctor) {
        return this.isMyToken(token);
      }

      return true;
    },

    async handleCallingTokenClick(token) {
      if (!token || String(token.status || '').toLowerCase() !== 'calling') return;

      await Swal.fire({
        title: `Token: ${token.token_number}`,
        text: 'Select an option to update status:',
        icon: 'question',
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        html: `
          <div class="d-grid gap-2 mt-3">
            <button id="swal-opt-serving" class="btn btn-info mb-2 w-100">Serving</button>
            <button id="swal-opt-skip" class="btn btn-secondary mb-2 w-100">Skip</button>
            <button id="swal-opt-pending" class="btn btn-warning mb-2 w-100">Waiting</button>
            <button id="swal-opt-calling" class="btn btn-primary mb-2 w-100">Calling</button>
          </div>
        `,
        didOpen: () => {
          const btnServing = document.getElementById('swal-opt-serving');
          const btnSkip = document.getElementById('swal-opt-skip');
          const btnPending = document.getElementById('swal-opt-pending');
          const btnCalling = document.getElementById('swal-opt-calling');

          if (btnServing) {
            btnServing.addEventListener('click', async () => {
              Swal.close();
              await this.updateCallingTokenStatus(token, 'serving');
            });
          }
          if (btnSkip) {
            btnSkip.addEventListener('click', async () => {
              Swal.close();
              await this.updateCallingTokenStatus(token, 'pending');
            });
          }
          if (btnPending) {
            btnPending.addEventListener('click', async () => {
              Swal.close();
              await this.updateCallingTokenStatus(token, 'pending');
            });
          }
          if (btnCalling) {
            btnCalling.addEventListener('click', async () => {
              Swal.close();
              await this.updateCallingTokenStatus(token, 'calling');
            });
          }
        }
      });
    },

    async updateCallingTokenStatus(token, newStatus) {
      try {
        const payload = {
          status: newStatus,
          updatedAt: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        // When moving to pending, refresh date & time to now so it moves to back of queue
        if (newStatus === 'pending') {
          payload.createdAt = new Date().toISOString();
        }

        await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/update/${token.id}`, payload, this.getAuthHeader());

        if (this.activeToken && this.activeToken.id === token.id && newStatus === 'pending') {
          this.activeToken = null;
        }

        // When moved to Serving, activate this token in the top Operator Panel
        if (newStatus === 'serving') {
          this.activeToken = { ...token, status: 'serving' };
          
          // Broadcast live 'Busy' status to dashboards
          const doctorStatusPayload = {
            doctor_id: token.user_id,
            user_id: token.user_id,
            counter_id: token.counter_id,
            doctor_name: token.token_attended,
            availability_status: 'Busy',
            status: 'Busy',
            currentToken: token.token_number,
            current_token: token.token_number
          };
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: doctorStatusPayload }));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: doctorStatusPayload }));
          localStorage.setItem('availability_status', 'Busy');
          localStorage.setItem('current_token', token.token_number);
          localStorage.setItem('doctor_status_synced', JSON.stringify({ ...doctorStatusPayload, timestamp: Date.now() }));
        } else if (newStatus === 'pending') {
          const resetPayload = {
            doctor_id: token.user_id,
            user_id: token.user_id,
            counter_id: token.counter_id,
            doctor_name: token.token_attended,
            availability_status: 'Available',
            status: 'Available',
            currentToken: '--',
            current_token: '--'
          };
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: resetPayload }));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: resetPayload }));
          localStorage.setItem('availability_status', 'Available');
          localStorage.setItem('current_token', '--');
          localStorage.setItem('doctor_status_synced', JSON.stringify({ ...resetPayload, timestamp: Date.now() }));
        }

        const displayLabel = newStatus === 'pending' ? 'waiting' : newStatus;
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: `Token ${token.token_number} status updated to ${displayLabel}`,
          showConfirmButton: false,
          timer: 2500
        });

        await this.fetchTokens();
      } catch (e) {
        Swal.fire("Error", e.response?.data?.message || "Failed to update status", "error");
      }
    },
    getCounterName(token) {
      if (!token) return '';
      if (token.counter && token.counter.name) return token.counter.name;
      if (token.counter_id && Array.isArray(this.countersList)) {
        const found = this.countersList.find(c => String(c.id) === String(token.counter_id));
        if (found) return found.name;
      }
      if (Array.isArray(this.countersList)) {
        const found = this.countersList.find(c => {
          const cUserId = c.user_id || (c.user ? c.user.id : null);
          const cName = String(c.user?.name || c.attendant_name || c.name || '').toLowerCase();
          const matchesId = cUserId && token.user_id && String(token.user_id) === String(cUserId);
          const matchesName = token.token_attended && token.token_attended !== 'No' && cName && String(token.token_attended).toLowerCase() === cName;
          return matchesId || matchesName;
        });
        if (found) return found.name;

        // Fallback: match by service_ids if token has a service_id
        if (token.service_id || (token.service && token.service.id)) {
          const sId = Number(token.service_id || token.service.id);
          const foundByService = this.countersList.find(c => {
            let sIds = c.service_ids;
            while (typeof sIds === 'string') {
              try {
                const parsed = JSON.parse(sIds);
                if (parsed === sIds) break;
                sIds = parsed;
              } catch (e) {
                sIds = sIds.split(',').map(s => s.trim());
                break;
              }
            }
            return Array.isArray(sIds) && sIds.map(Number).includes(sId);
          });
          if (foundByService) return foundByService.name;
        }
      }
      return '';
    },

    // ------------------------------------------------------------------
    // DRAG AND DROP HANDLERS
    // ------------------------------------------------------------------
    onDragStart(e, token) {
      if (!this.canDragToken(token)) {
        e.preventDefault();
        return;
      }
      this.draggedToken = token;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', token.id);
    },
    onDragOver(columnId) {
      if (!this.canDragDrop || !this.draggedToken) return;
      this.dragOverColumnId = columnId;
    },
    onDragLeave() {
      this.dragOverColumnId = null;
    },
    async onDrop(target) {
      if (!this.canDragDrop || !this.draggedToken) return;

      const token = this.draggedToken;

      // DOCTOR RESTRICTION: Ensure doctor is only reassigning their own token (Unrestricted roles like admin, superadmin, tokenmanager, receptionist bypass)
      if (!this.isUnrestrictedManager && this.isDoctor && !this.isMyToken(token)) {
        this.draggedToken = null;
        this.dragOverColumnId = null;
        return Swal.fire("Permission Denied", "You can only reassign tokens that are assigned to your own counter.", "warning");
      }

      this.dragOverColumnId = null;

      const isUnassign = target === 'unassigned';
      const targetCounterId = isUnassign ? null : target.id;
      const targetName = isUnassign ? 'Unassigned Queue' : (target.name || 'Counter');

      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to move token ${token.token_number} to ${targetName}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, move it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: this.getDynamicColor() || '#5e72e4',
        cancelButtonColor: '#d33'
      });

      if (!result.isConfirmed) {
        this.draggedToken = null;
        return;
      }

      this.draggedToken = null;

      const newStatus = isUnassign ? 'pending' : 'calling';

      try {
        const payload = {
          tokenId: token.id,
          counterId: targetCounterId,
          unassign: isUnassign,
          status: newStatus
        };

        const res = await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/transfer-counter`, payload, this.getAuthHeader());
        
        // Explicitly ensure status is updated to 'calling' on the token record
        let updatedAttendant = 'No';
        let updatedUserId = null;
        if (!isUnassign && target) {
          updatedUserId = target.user_id || (target.user ? target.user.id : null);
          updatedAttendant = target.user ? target.user.name : (target.attendant_name || target.name);
        }

        await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/update/${token.id}`, {
          counter_id: targetCounterId,
          user_id: updatedUserId,
          token_attended: updatedAttendant,
          status: newStatus
        }, this.getAuthHeader()).catch((e) => {
          console.warn("Direct token status update notice:", e);
        });

        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: res.data?.message || `Token ${token.token_number} moved to ${isUnassign ? 'Unassigned' : target.name} (Status: ${newStatus.toUpperCase()})`,
          showConfirmButton: false,
          timer: 2500
        });

        await this.fetchTokens();
      } catch (err) {
        // Fallback: If transfer-counter endpoint isn't routed yet, update token directly
        try {
          let updatedAttendant = 'No';
          let updatedUserId = null;

          if (!isUnassign && target) {
            updatedUserId = target.user_id || (target.user ? target.user.id : null);
            updatedAttendant = target.user ? target.user.name : (target.attendant_name || target.name);
          }

          await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/update/${token.id}`, {
            counter_id: targetCounterId,
            user_id: updatedUserId,
            token_attended: updatedAttendant,
            status: newStatus
          }, this.getAuthHeader());

          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: `Token moved to ${isUnassign ? 'Unassigned' : target.name} (Status: ${newStatus.toUpperCase()})`,
            showConfirmButton: false,
            timer: 2500
          });

          await this.fetchTokens();
        } catch (e) {
          Swal.fire("Error", "Failed to transfer token across counters", "error");
        }
      }
    },

    getTokensForCounter(counter) {
      if (!Array.isArray(this.tokens)) return [];
      let activeOnly = this.tokens.filter(t => String(t.status || '').toLowerCase() !== 'completed');

      const cleanedRole = String(this.userRole || localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      const isQueueManager = this.isAdminOrSuperAdmin || ['tokenmanager', 'tokenmanger', 'receptionist', 'reception', 'tokenuser', 'operator', 'teller', 'staff', 'nurse', '1', '2'].some(r => cleanedRole.includes(r));

      // For non-admin roles, restrict board view to only their own tokens + emergency tokens + unassigned queue
      if (!isQueueManager) {
        const currentUserId = String(localStorage.getItem('id') || localStorage.getItem('userId') || '');
        const currentUserName = String(localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase().trim();
        const cleanDocName = currentUserName.replace(/^dr\.?\s*/i, '').trim();

        const myCounters = (this.countersList || []).filter(c => {
          const cUserId = String(c.user_id || (c.user ? c.user.id : '') || '');
          const cAttName = String(c.user?.name || c.attendant_name || '').toLowerCase().trim();
          const cleanAttName = cAttName.replace(/^dr\.?\s*/i, '').trim();
          const matchesUser = currentUserId && cUserId === currentUserId;
          const matchesAttendant = currentUserName && (cAttName === currentUserName || (cleanDocName && cleanAttName.includes(cleanDocName)));
          return matchesUser || matchesAttendant;
        });
        const myCounterIds = myCounters.map(c => String(c.id));

        activeOnly = activeOnly.filter(t => {
          const isEm = t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true' || String(t.is_emergency || '').toLowerCase() === 'yes';
          if (isEm) return true;

          const uId = String(t.user_id || '');
          const cId = String(t.counter_id || '');
          const tAttName = String(t.token_attended || '').toLowerCase().trim();
          const cleanTAtt = tAttName.replace(/^dr\.?\s*/i, '').trim();

          const matchesUser = currentUserId && uId === currentUserId;
          const matchesCounter = cId && myCounterIds.includes(cId);
          const matchesAttendant = currentUserName && tAttName !== 'no' && (tAttName === currentUserName || (cleanDocName && cleanTAtt.includes(cleanDocName)));
          const isUnassigned = (!cId || cId === '0') && (!uId || uId === '0' || !tAttName || tAttName === 'no');

          return matchesUser || matchesCounter || matchesAttendant || isUnassigned;
        });
      }

      // 1. Unassigned / Pool Tokens Column
      if (!counter) {
        return activeOnly.filter(t => {
          const isExplicitlyAssigned = (t.counter_id && String(t.counter_id) !== '0') || 
                                       (t.user_id && String(t.user_id) !== '0' && t.token_attended && t.token_attended !== 'No');
          return !isExplicitlyAssigned;
        });
      }

      // 2. Specific Counter Columns (ONLY show tokens actively assigned to THIS counter)
      const cUserId = counter.user_id || (counter.user ? counter.user.id : null);
      const cName = String(counter.user?.name || counter.attendant_name || counter.name || '').toLowerCase();

      return activeOnly.filter(t => {
        // If the token is not assigned, it belongs ONLY in the Unassigned Queue
        const isExplicitlyAssigned = (t.counter_id && String(t.counter_id) !== '0') || 
                                     (t.user_id && String(t.user_id) !== '0' && t.token_attended && t.token_attended !== 'No');
        if (!isExplicitlyAssigned) return false;

        const matchesCounterId = t.counter_id && String(t.counter_id) === String(counter.id);
        const matchesId = cUserId && t.user_id && String(t.user_id) === String(cUserId);
        // FIXED: Use t.token_attended instead of undefined token.token_attended
        const matchesName = t.token_attended && t.token_attended !== 'No' && cName && String(t.token_attended).toLowerCase() === cName;

        return matchesCounterId || matchesId || matchesName;
      });
    },

    async fetchCounters() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/counter?all=true`, this.getAuthHeader());
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          this.countersList = res.data.data;
        }
      } catch (e) {
        console.error("Error loading counters:", e);
      }
    },

    // Role & Action Permissions Guard
    checkPermission(action) {
      const cleanedRole = String(this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      
      // 1. Admin & Super Admin always have full access
      if (['admin', 'superadmin', '1', '2'].includes(cleanedRole)) {
        return true;
      }

      // 2. Token Manager & Receptionist built-in full access
      if (cleanedRole === 'tokenmanager' || cleanedRole.includes('tokenmanager') || cleanedRole.includes('receptionist')) {
        return true;
      }

      // 3. Token User / Operator can ALWAYS add tokens (create) and call queue tokens
      if (cleanedRole.includes('tokenuser') || cleanedRole.includes('operator')) {
        return action === 'create';
      }

      // 4. Dynamic Permissions check from localStorage
      let perms = [];
      const rawUserModules = localStorage.getItem('userModules') || localStorage.getItem('allowedModules') || localStorage.getItem('permissions');
      if (rawUserModules) {
        try {
          const parsed = JSON.parse(rawUserModules);
          if (Array.isArray(parsed)) perms = parsed;
        } catch (e) {
          console.error("Error parsing user modules:", e);
        }
      }

      if (perms.length === 0) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const userObj = JSON.parse(userStr);
            const userPerms = userObj?.role?.permissions || userObj?.permissions || userObj?.modules;
            if (Array.isArray(userPerms)) {
              perms = userPerms;
            } else if (typeof userPerms === 'string') {
              try {
                perms = JSON.parse(userPerms);
              } catch (e) {
                console.error("Error parsing permissions string:", e);
              }
            }
          } catch (e) {
            console.error("Error parsing user object:", e);
          }
        }
      }

      if (Array.isArray(perms) && perms.length > 0) {
        const lowerPerms = perms.map(p => String(p).toLowerCase().trim());
        const hasTokenModule = lowerPerms.some(p => p.includes('token') || p === '19' || p === '4');
        if (hasTokenModule) {
          const hasAction = lowerPerms.some(p => 
            p === `tokens:${action}` || 
            p === 'tokens:*' || 
            p === '*:*' || 
            p.includes('token') ||
            p === '*'
          );
          if (hasAction) return true;
        }
      }

      // 5. Built-in Role Fallback if no dynamic action permissions are set:
      if (cleanedRole.includes('doctor')) {
        return action === 'create' || action === 'update';
      }

      return false;
    },

    // Label display helper: shows "SERVING" for processing/calling status and "WAITING" for pending
    displayStatus(status) {
     const s = String(status || '').toLowerCase();
      if (s === 'serving' || s === 'processing') return 'SERVING';
      if (s === 'calling') return 'CALLING';
      if (s === 'pending') return 'WAITING';
      return s.toUpperCase();
    },

    // Calculates next 4-digit token number PER SPECIFIC SERVICE AND TARGET DATE (e.g. AO-0001, CD-0001)
    generateNext4DigitTokenNumber(service = null, targetDate = null) {
      if (!service) return '';

      const servicePrefix = (service.prefix || '').trim().toUpperCase();
      const serviceId = service.id;

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      const effectiveDate = targetDate || todayStr;

      // Filter tokens specifically belonging to this service (supports ID match and prefix match) AND TARGET DATE
      const serviceTokens = this.tokens.filter(t => {
        const sameId = (t.service_id && String(t.service_id) === String(serviceId)) ||
                       (t.service && String(t.service.id) === String(serviceId));
        const prefixMatch = servicePrefix && t.token_number && t.token_number.toString().toUpperCase().startsWith(servicePrefix);
        if (!sameId && !prefixMatch) return false;

        let tDate = t.booking_date;
        if (!tDate) {
          const raw = t.createdAt || t.created_at;
          if (raw) {
            try {
              const d = new Date(raw);
              if (!isNaN(d.getTime())) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const dt = String(d.getDate()).padStart(2, '0');
                tDate = `${y}-${m}-${dt}`;
              }
            } catch (err) {
              console.debug("Date parse fallback:", err);
            }
          }
        }
        return tDate === effectiveDate;
      });

      const highestNum = serviceTokens.reduce((max, t) => {
        const parsed = parseInt((t.token_number || '').toString().replace(/\D/g, ''), 10);
        return !isNaN(parsed) && parsed > max ? parsed : max;
      }, 0);

      const nextSeq = String(highestNum + 1).padStart(4, '0');
      const prefix = service.prefix ? service.prefix.trim() : '';
      if (prefix) {
        return prefix.endsWith('-') ? `${prefix}${nextSeq}` : `${prefix}-${nextSeq}`;
      }
      return nextSeq;
    },

    // Helper to resolve next available token number with backend fallback for accurate per-date indexing
    async resolveNextTokenNumber(service, targetDate) {
      if (!service) return '';
      try {
        const res = await axios.get(
          `${this.apiBaseUrl}/v1/admin/tokens/next-number?service_id=${service.id}&date=${targetDate}`,
          this.getAuthHeader()
        );
        if (res.data && res.data.success && res.data.token_number) {
          return res.data.token_number;
        }
      } catch (err) {
        console.debug("Remote sequence fetch notice:", err);
      }
      return this.generateNext4DigitTokenNumber(service, targetDate);
    },

    // --- SERVICE TILE DESIGN METHODS ---
    getTileStyle(item) {
      if (!item.status) return {}; 
      return {
        background: `linear-gradient(135deg, ${this.activeTheme} 0%, ${this.adjustColor(this.activeTheme, 20)} 100%)`
      };
    },
    adjustColor(hex, percent) {
      var num = parseInt(hex.replace("#",""),16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = (num >> 8 & 0x00FF) + amt,
      B = (num >> 8 & 0x0000FF) + amt;
      return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
    },
    getServiceIcon(name) {
      const lower = (name || '').toLowerCase();
      if (lower.includes('doctor') || lower.includes('opd') || lower.includes('consult')) return 'fas fa-user-md';
      if (lower.includes('cardio') || lower.includes('heart')) return 'fas fa-heartbeat';
      if (lower.includes('ortho') || lower.includes('bone')) return 'fas fa-bone';
      if (lower.includes('radio') || lower.includes('x-ray') || lower.includes('mri')) return 'fas fa-x-ray';
      if (lower.includes('patho') || lower.includes('lab') || lower.includes('blood')) return 'fas fa-vial';
      if (lower.includes('pharma') || lower.includes('medicine')) return 'fas fa-pills';
      if (lower.includes('emerg') || lower.includes('trauma')) return 'fas fa-ambulance';
      if (lower.includes('dental') || lower.includes('tooth')) return 'fas fa-tooth';
      if (lower.includes('eye') || lower.includes('ophta')) return 'fas fa-eye';
      if (lower.includes('child') || lower.includes('pedia')) return 'fas fa-baby';
      if (lower.includes('internet')) return 'fas fa-globe';
      if (lower.includes('tv')) return 'fas fa-tv';
      if (lower.includes('power') || lower.includes('elect')) return 'fas fa-bolt';
      if (lower.includes('water')) return 'fas fa-tint';
      if (lower.includes('gas')) return 'fas fa-fire';
      if (lower.includes('phone') || lower.includes('mobile')) return 'fas fa-phone';
      if (lower.includes('account')) return 'fas fa-university';
      if (lower.includes('withdraw')) return 'fas fa-money-bill-wave';
      if (lower.includes('cash') || lower.includes('deposit')) return 'fas fa-money-bill';
      if (lower.includes('customer') || lower.includes('service')) return 'fas fa-concierge-bell';
      return 'fas fa-th-large';
    },

    /**
     * TILE CLICK ACTION: Pre-selects the clicked service and fills per-service token number
     */
    onServiceTileClick(service) {
      if (!service.status) {
        return Swal.fire("Inactive Service", "This service is currently inactive.", "warning");
      }
      this.handleAction(null, service);
    },

    detectRole() {
      let role = localStorage.getItem("userRole") || localStorage.getItem("user_role") || localStorage.getItem("role");
      if (!role) {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            role = user.role_name || (user.role && typeof user.role === 'object' ? user.role.name : user.role);
          } catch (e) { console.error("Error parsing user"); }
        }
      }
      this.userRole = (role || "").toString().replace(/['"]+/g, '').trim();
    },
    getAuthHeader() {
      const token = localStorage.getItem("token");
      return { headers: { Authorization: `Bearer ${token}` } };
    },
    getDynamicColor() {
      return localStorage.getItem('themeColor') || '#5e72e4'; 
    },
     getStatusClass(status) {
      const s = String(status || '').toLowerCase();
      switch(s) {
        case 'completed': return 'bg-gradient-success'; // Green
        case 'pending': return 'bg-gradient-warning';   // Orange
        case 'calling': return 'bg-gradient-primary';   // Purple/Blue (Called to counter)
        case 'serving': 
        case 'processing': return 'bg-gradient-info';   // Cyan/Blue (Actively being served)
        case 'skipped': return 'bg-gradient-secondary'; // Grey
        case 'rejected': return 'bg-gradient-danger';   // Red
        default: return 'bg-gradient-secondary';
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    async fetchServices() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/services/list`, this.getAuthHeader());
        if (res.data && res.data.success) {
          this.servicesList = res.data.data;
        }
      } catch (e) { console.error("Error fetching services:", e); }
    },
    async fetchAttendants() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/user/attendants`, this.getAuthHeader());
        if (res.data && res.data.success) {
          this.attendants = res.data.data;
        }
      } catch (e) { console.error("Error fetching attendants:", e); }
    },

    async handleCallNextTrigger() {
      const themeColor = this.getDynamicColor();

      // Check if there are any pending emergency tokens in the queue (for doctors, only their emergency tokens)
      const emergencyTokens = this.filteredTokens.filter(t => 
        (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true') && 
        String(t.status || '').toLowerCase() === 'pending'
      );
      const hasEmergency = emergencyTokens.length > 0;

      const result = await Swal.fire({
        title: 'Select Calling Method',
        text: 'How would you like to call the next token?',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: hasEmergency,
        cancelButtonText: 'Search & Pick Manual',
        confirmButtonText: 'Call Next (Queue)',
        denyButtonText: `<i class="fas fa-ambulance me-1"></i> Call Emergency (${emergencyTokens.length})`,
        cancelButtonColor: '#172b4d',
        confirmButtonColor: themeColor,
        denyButtonColor: '#dc3545',
        reverseButtons: true,
        customClass: {
          actions: 'd-flex flex-wrap justify-content-center gap-2'
        }
      });

      if (result.isConfirmed) {
        this.pullNextToken(false);
      } else if (result.isDenied) {
        this.pullNextToken(true);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        this.handleManualSearchAndPick();
      }
    },

    async pullNextToken(emergencyOnly = false) {
      try {
        const payload = emergencyOnly ? { emergency_only: true } : {};
        const res = await axios.post(`${this.apiBaseUrl}/v1/admin/tokens/call-next`, payload, this.getAuthHeader());
        if (res.data.success && res.data.data) {
          this.activeToken = res.data.data;
          this.fetchTokens(); 

          // >>> DISPATCH LIVE STATUS AS BUSY TO MEDICAL DASHBOARDS <<<
          const doctorStatusPayload = {
            doctor_id: this.activeToken.user_id,
            user_id: this.activeToken.user_id,
            counter_id: this.activeToken.counter_id,
            doctor_name: this.activeToken.token_attended,
            availability_status: 'Busy',
            status: 'Busy',
            currentToken: this.activeToken.token_number,
            current_token: this.activeToken.token_number
          };
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: doctorStatusPayload }));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: doctorStatusPayload }));
          localStorage.setItem('availability_status', 'Busy');
          localStorage.setItem('current_token', this.activeToken.token_number);
          localStorage.setItem('doctor_status_synced', JSON.stringify({ ...doctorStatusPayload, timestamp: Date.now() }));

          Swal.fire({ 
            title: emergencyOnly ? `🚨 Emergency Called: ${this.activeToken.token_number}` : `Called Token: ${this.activeToken.token_number}`, 
            text: `Name: ${this.activeToken.token_name || 'Walk-in'}`,
            icon: emergencyOnly ? 'warning' : 'success', 
            toast: true, 
            position: 'top-end', 
            showConfirmButton: false, 
            timer: 3500 
          });
        } else {
          Swal.fire(
            emergencyOnly ? "No Emergency Tokens" : "Queue Empty", 
            res.data?.message || (emergencyOnly ? "No pending emergency tokens found in the queue." : "No pending tokens found in the FIFO queue."), 
            "info"
          );
        }
      } catch (e) {
        // Fallback safeguard: If endpoint does not process emergency_only flag, pick up direct emergency token ID
        // Sort by earliest created first to strictly adhere to FIFO
        if (emergencyOnly) {
          const emergencyTokens = this.filteredTokens.filter(t => 
            (t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true') && 
            String(t.status || '').toLowerCase() === 'pending'
          );
          if (emergencyTokens.length > 0) {
            emergencyTokens.sort((a, b) => new Date(a.createdAt || a.created_at || 0) - new Date(b.createdAt || b.created_at || 0));
            return this.pickUpToken(emergencyTokens[0].id);
          }
        }
        Swal.fire("Error", e.response?.data?.message || "Failed to call next token", "error");
      }
    },

    async handleManualSearchAndPick() {
      const themeColor = this.getDynamicColor();
      const currentUserId = localStorage.getItem('id') || localStorage.getItem('userId');
      const currentUserName = (localStorage.getItem('userName') || localStorage.getItem('name') || '').toLowerCase();
      const cleanDocName = currentUserName.replace(/^dr\.?\s*/i, '').trim();
      const cleanedRole = String(this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
      const isManager = ['admin', 'superadmin', 'tokenmanager', 'receptionist', '1', '2'].some(r => cleanedRole.includes(r));

      // Collect doctor/staff's counter services
      let myServiceIds = [];
      if (Array.isArray(this.countersList)) {
        this.countersList.forEach(c => {
          const cUserId = String(c.user_id || (c.user ? c.user.id : '') || '');
          const cAttName = String(c.user?.name || c.attendant_name || '').toLowerCase().trim();
          const matchesUser = currentUserId && cUserId === String(currentUserId);
          const matchesAttendant = currentUserName && (cAttName === currentUserName || (cleanDocName && cAttName.includes(cleanDocName)));
          if (matchesUser || matchesAttendant) {
            let sIds = c.service_ids;
            while (typeof sIds === 'string') {
              try {
                const parsed = JSON.parse(sIds);
                if (parsed === sIds) break;
                sIds = parsed;
              } catch (e) {
                sIds = sIds.split(',').map(s => s.trim());
                break;
              }
            }
            if (Array.isArray(sIds)) myServiceIds.push(...sIds.map(Number));
          }
        });
      }

      // Allow pending tokens that are unassigned, or assigned to the logged-in attendant, or all pending tokens for managers
      // For doctor: allow tokens strictly assigned to this doctor, their counter, OR unassigned tokens matching their counter services
      const availableTokens = this.filteredTokens.filter(t => {
        const isPending = String(t.status || '').toLowerCase() === 'pending';
        if (!isPending) return false;

        // EMERGENCY TOKENS: Always allow any doctor to view and pick emergency cases
        const isEm = t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true' || String(t.is_emergency || '').toLowerCase() === 'yes';
        if (isEm) return true;

        if (this.isDoctor) {
          const isAssignedToMe = (currentUserId && String(t.user_id) === String(currentUserId)) ||
                                 (t.token_attended && t.token_attended.toLowerCase() === currentUserName);
          const tServiceId = Number(t.service_id || (t.service ? t.service.id : 0));
          const isUnassignedMatchingService = (!t.user_id || t.user_id === '0' || String(t.token_attended).toLowerCase() === 'no' || !t.token_attended) && myServiceIds.includes(tServiceId);
          return isAssignedToMe || isUnassignedMatchingService;
        }

        if (isManager) return true;

        const isUnassigned = !t.user_id || t.token_attended === 'No' || !t.token_attended;
        const isAssignedToMe = (currentUserId && String(t.user_id) === String(currentUserId)) ||
                               (t.token_attended && t.token_attended.toLowerCase() === currentUserName);

        return isUnassigned || isAssignedToMe;
      });
      
      if (availableTokens.length === 0) {
        return Swal.fire("No Available Tokens", "All pending tokens are currently being served or the queue is empty.", "info");
      }

      // Sort emergency tokens to the top of the search list (ESLint clean comparison)
      // When multiple emergency tokens exist, order them by oldest arrival first (FIFO order)
      availableTokens.sort((a, b) => {
        const emA = (a.is_emergency === true || a.is_emergency === 1 || a.is_emergency === 'true') ? 1 : 0;
        const emB = (b.is_emergency === true || b.is_emergency === 1 || b.is_emergency === 'true') ? 1 : 0;
        if (emB !== emA) return emB - emA;

        const timeA = new Date(a.createdAt || a.created_at || 0).getTime();
        const timeB = new Date(b.createdAt || b.created_at || 0).getTime();
        return timeA - timeB;
      });

      let tokenListItems = ``;
      availableTokens.forEach(t => {
        const isEm = t.is_emergency === true || t.is_emergency === 1 || t.is_emergency === 'true';
        const attendedText = t.token_attended && t.token_attended !== 'No' ? ` (Assigned: ${t.token_attended})` : '';
        const emergencyBadge = isEm ? `<span class="badge bg-danger ms-2 text-xxs"><i class="fas fa-ambulance me-1"></i>EMERGENCY</span>` : '';
        const timeStr = (t.createdAt || t.created_at) ? new Date(t.createdAt || t.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
        const phoneStr = t.phone_number ? ` • Tel: ${t.phone_number}` : '';
        const ageStr = (t.age !== null && t.age !== undefined && t.age !== '') ? ` • Age: ${t.age}` : '';
        const serviceStr = t.service?.name ? ` • ${t.service.name}` : '';

        tokenListItems += `<div class="token-search-item p-2 d-flex justify-content-between align-items-center" 
          data-id="${t.id}" 
          data-search="${t.token_number} ${t.token_name || ''} ${t.phone_number || ''} ${t.service?.name || ''} ${isEm ? 'emergency' : ''}" 
          style="cursor:pointer; border-bottom:1px solid #f0f2f5; ${isEm ? 'background-color: #fff5f5;' : ''}">
            <div>
              <div class="d-flex align-items-center">
                <strong class="${isEm ? 'text-danger' : 'text-dark'}">${t.token_number}</strong> - <span class="ms-1 fw-bold">${t.token_name || 'Walk-in'}</span> ${emergencyBadge}
              </div>
              <small class="text-muted d-block text-xxs mt-1">
                <i class="far fa-clock me-1"></i>${timeStr}${serviceStr}${ageStr}${phoneStr}
              </small>
            </div>
            <div class="text-end">
              <small class="text-muted d-block">${attendedText}</small>
              <span class="badge ${isEm ? 'bg-danger text-white' : 'bg-light text-primary'} mt-1">Select</span>
            </div>
        </div>`;
      });

      await Swal.fire({
        title: 'Search & Pick Token',
        html: `
          <div class="text-start">
            <label class="form-label text-xs fw-bold text-uppercase text-secondary">Search Patient, Phone or Token Number</label>
            <div class="position-relative mb-3">
              <div class="input-group">
                <span class="input-group-text bg-white border-end-0"><i class="fas fa-search text-xs text-secondary"></i></span>
                <input type="text" id="swal-manual-token-search" class="form-control border-start-0" placeholder="Type name, token number, phone, or 'emergency'..." autocomplete="off">
              </div>
              
              <div id="manual-token-dropdown" class="shadow-lg border rounded bg-white w-100 mt-1" style="display:block; max-height:240px; overflow-y:auto;">
                ${tokenListItems}
              </div>
            </div>
          </div>
        `,
        confirmButtonColor: themeColor,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        didOpen: () => {
          const searchInput = document.getElementById('swal-manual-token-search');
          const dropdownList = document.getElementById('manual-token-dropdown');
          const items = dropdownList.querySelectorAll('.token-search-item');

          searchInput.focus();

          searchInput.addEventListener('input', (e) => {
            const term = e.target.value.toLowerCase().trim();
            items.forEach(item => {
              const text = item.getAttribute('data-search').toLowerCase();
              item.style.display = text.includes(term) ? 'flex' : 'none';
            });
          });

          dropdownList.addEventListener('mousedown', (e) => {
            const target = e.target.closest('.token-search-item');
            if (target) {
              const id = target.getAttribute('data-id');
              Swal.close();
              this.pickUpToken(id);
            }
          });
        }
      });
    },

    async pickUpToken(tokenId) {
      try {
        const res = await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/assign/${tokenId}`, {}, this.getAuthHeader());
        if (res.data.success && res.data.data) {
          this.activeToken = res.data.data;
          this.fetchTokens();

          // >>> DISPATCH LIVE STATUS AS BUSY TO MEDICAL DASHBOARDS <<<
          const doctorStatusPayload = {
            doctor_id: this.activeToken.user_id,
            user_id: this.activeToken.user_id,
            counter_id: this.activeToken.counter_id,
            doctor_name: this.activeToken.token_attended,
            availability_status: 'Busy',
            status: 'Busy',
            currentToken: this.activeToken.token_number,
            current_token: this.activeToken.token_number
          };
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: doctorStatusPayload }));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: doctorStatusPayload }));
          localStorage.setItem('availability_status', 'Busy');
          localStorage.setItem('current_token', this.activeToken.token_number);
          localStorage.setItem('doctor_status_synced', JSON.stringify({ ...doctorStatusPayload, timestamp: Date.now() }));

          Swal.fire({ 
            title: 'Token Assigned', 
            text: `You are now serving ${this.activeToken.token_number}`, 
            icon: 'success', 
            toast: true, 
            position: 'top-end', 
            showConfirmButton: false, 
            timer: 3000 
          });
        }
      } catch (e) {
        Swal.fire("Error", e.response?.data?.message || "Failed to pick up token", "error");
      }
    },

    async handleCounterAction(action) {
      if (!this.activeToken) return;
      try {
        const finishedToken = { ...this.activeToken };
        const res = await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/${action}/${this.activeToken.id}`, {}, this.getAuthHeader());
        if (res.data.success) {
          this.activeToken = null;
          this.fetchTokens();

          // >>> RESET LIVE STATUS AS AVAILABLE ON DASHBOARDS <<<
          const resetPayload = {
            doctor_id: finishedToken.user_id,
            user_id: finishedToken.user_id,
            counter_id: finishedToken.counter_id,
            doctor_name: finishedToken.token_attended,
            availability_status: 'Available',
            status: 'Available',
            currentToken: '--',
            current_token: '--'
          };
          window.dispatchEvent(new CustomEvent('doctor-status-synced', { detail: resetPayload }));
          window.dispatchEvent(new CustomEvent('doctor-availability-changed', { detail: resetPayload }));
          localStorage.setItem('availability_status', 'Available');
          localStorage.setItem('current_token', '--');
          localStorage.setItem('doctor_status_synced', JSON.stringify({ ...resetPayload, timestamp: Date.now() }));
        }
      } catch (e) {
        Swal.fire("Error", "Action failed", "error");
      }
    },

    async checkServingStatus() {
      try {
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/tokens/active-serving`, this.getAuthHeader());
        if (res.data.success && res.data.data) {
          this.activeToken = res.data.data;
        }
      } catch (e) { console.error("Error checking serving status"); }
    },

    clearDateFilter() {
      this.selectedDate = "";
      this.fetchTokens();
    },

    async fetchTokens() {
      try {
        const statusParam = this.showingExpired ? 'expired' : 'today';
        let url = `${this.apiBaseUrl}/v1/admin/tokens/list?status=${statusParam}&view=board&all=true`;

        if (this.selectedDate) {
          url += `&date=${this.selectedDate}&booking_date=${this.selectedDate}`;
        }
        if (this.tokenSearchQuery && this.tokenSearchQuery.trim()) {
          const q = encodeURIComponent(this.tokenSearchQuery.trim());
          url += `&search=${q}&q=${q}`;
        }

        const res = await axios.get(url, this.getAuthHeader());
        const resData = res.data;
        let rawTokens = [];
        if (Array.isArray(resData)) {
          rawTokens = resData;
        } else if (resData && resData.data) {
          if (Array.isArray(resData.data)) {
            rawTokens = resData.data;
          } else if (Array.isArray(resData.data.tokens)) {
            rawTokens = resData.data.tokens;
          } else if (Array.isArray(resData.data.rows)) {
            rawTokens = resData.data.rows;
          } else if (Array.isArray(resData.data.result)) {
            rawTokens = resData.data.result;
          }
        } else if (resData && Array.isArray(resData.tokens)) {
          rawTokens = resData.tokens;
        } else if (resData && Array.isArray(resData.rows)) {
          rawTokens = resData.rows;
        } else if (resData && Array.isArray(resData.result)) {
          rawTokens = resData.result;
        }
        this.tokens = rawTokens;
      } catch (error) { 
        console.error("fetchTokens error:", error); 
        this.tokens = [];
      }
    },
    toggleExpired() {
      this.showingExpired = !this.showingExpired;
      this.selectedDate = ""; 
      this.selectedStartTime = "";
      this.selectedEndTime = "";
      this.fetchTokens();
    },
    clearTimeFilter() {
      this.selectedStartTime = "";
      this.selectedEndTime = "";
    },
    onSearchInput() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.fetchTokens();
      }, 350);
    },

    async openTokenHistory(token) {
      this.selectedToken = { ...token };
      
      // If token already has embedded history from getList, use it directly
      if (token.history && Array.isArray(token.history) && token.history.length > 0) {
        this.tokenHistoryList = token.history;
        this.selectedToken = {
          ...token,
          visit_count: token.history.length
        };
        this.showHistoryModal = true;
        return;
      }

      // If token has visited_counters array from getList, format it as history fallback
      if (token.visited_counters && Array.isArray(token.visited_counters) && token.visited_counters.length > 0) {
        this.tokenHistoryList = token.visited_counters;
      }

      // Fetch from dedicated history endpoint
      let historyData = null;
      try {
        const url = `${this.apiBaseUrl}/v1/admin/tokens/${token.id}/history`;
        const res = await axios.get(url, this.getAuthHeader());
        if (res.data && res.data.success && res.data.data) {
          historyData = res.data.data;
        }
      } catch (err) {
        // Fallback: try with /api prefix if direct failed
        try {
          const resFallback = await axios.get(`/api/v1/admin/tokens/${token.id}/history`, this.getAuthHeader());
          if (resFallback.data && resFallback.data.success && resFallback.data.data) {
            historyData = resFallback.data.data;
          }
        } catch (e) {
          console.error("Failed to load token history", e);
        }
      }

      if (historyData) {
        const list = historyData.history || historyData.visited_counters || this.tokenHistoryList;
        this.tokenHistoryList = list;
        const totalVisits = (list && list.length > 0) ? list.length : (historyData.visit_count || token.visit_count || 1);
        this.selectedToken = {
          ...token,
          visit_count: totalVisits,
          visited_counters: historyData.visited_counters || token.visited_counters
        };
      }
      this.showHistoryModal = true;
    },

    closeTokenHistory() {
      this.showHistoryModal = false;
      this.selectedToken = null;
      this.tokenHistoryList = [];
    },

    exportToExcel() {
      if (!this.filteredTokens || this.filteredTokens.length === 0) {
        return Swal.fire("No Data", "No tokens found to export for the selected date and time.", "info");
      }

      const isDoc = this.isDoctor;
      const exportData = this.filteredTokens.map((t, idx) => ({
        "Sr. No.": idx + 1,
        "Token Number": t.token_number || "-",
        "Service": t.service?.name || t.service_name || "N/A",
        [isDoc ? "Patient Name" : "User Name"]: t.token_name || (isDoc ? "Walk-in Patient" : "Walk-in"),
        "Age": t.age !== null && t.age !== undefined && t.age !== "" ? t.age : "-",
        "Gender": t.gender || "-",
        "Phone Number": t.phone_number || "-",
        "Counter": this.getCounterName(t) || "-",
        "Booking Date": t.booking_date || "-",
        "Booking Time": t.booking_time || "-",
        "Added At": this.formatDateTime(t.createdAt || t.created_at),
        [isDoc ? "Attended By (Doctor)" : "Attended By"]: t.token_attended && t.token_attended !== "No" ? t.token_attended : (t.user?.name || t.attendant_name || "No"),
        "Status": this.displayStatus(t.status)
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tokens Report");

      const dateTag = this.selectedDate || new Date().toISOString().slice(0, 10);
      const timeTag = (this.selectedStartTime || this.selectedEndTime)
        ? `_${(this.selectedStartTime || '00-00').replace(':', '-')}_to_${(this.selectedEndTime || '23-59').replace(':', '-')}`
        : '';
      const filename = `Tokens_Report_${dateTag}${timeTag}.xlsx`;

      XLSX.writeFile(workbook, filename);
    },

    exportToPDF() {
      if (!this.filteredTokens || this.filteredTokens.length === 0) {
        return Swal.fire("No Data", "No tokens found to export for the selected date and time.", "info");
      }

      const doc = new jsPDF("l", "mm", "a4");
      const isDoc = this.isDoctor;

      const dateTag = this.selectedDate || new Date().toISOString().slice(0, 10);
      let timeStr = "";
      if (this.selectedStartTime && this.selectedEndTime) {
        timeStr = ` | Time: ${this.selectedStartTime} - ${this.selectedEndTime}`;
      } else if (this.selectedStartTime) {
        timeStr = ` | From Time: ${this.selectedStartTime}`;
      } else if (this.selectedEndTime) {
        timeStr = ` | Up to Time: ${this.selectedEndTime}`;
      }

      const title = `Token Queue Report (${this.showingExpired ? "Historical Tokens" : "Active Tokens Today"})`;
      const subtitle = `Filter Date: ${dateTag}${timeStr}  |  Total Records: ${this.filteredTokens.length}  |  Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;

      doc.setFontSize(14);
      doc.setTextColor(30, 58, 95);
      doc.text(title, 14, 13);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(subtitle, 14, 19);

      const heads = [
        ["#", "Token", "Service", isDoc ? "Patient Name" : "User Name", "Age", "Phone", "Counter", "Booking Schedule", "Added At", isDoc ? "Doctor" : "Attended By", "Status"]
      ];

      const rows = this.filteredTokens.map((t, idx) => [
        idx + 1,
        t.token_number || "-",
        t.service?.name || t.service_name || "N/A",
        t.token_name || (isDoc ? "Walk-in Patient" : "Walk-in"),
        (t.age !== null && t.age !== undefined && t.age !== "" ? String(t.age) : "-") + (t.gender ? ` (${t.gender})` : ""),
        t.phone_number || "-",
        this.getCounterName(t) || "-",
        (t.booking_date || t.booking_time) ? `${t.booking_date || ''} ${t.booking_time || ''}`.trim() : "-",
        this.formatDateTime(t.createdAt || t.created_at),
        t.token_attended && t.token_attended !== "No" ? t.token_attended : (t.user?.name || t.attendant_name || "No"),
        this.displayStatus(t.status)
      ]);

      const tableOptions = {
        startY: 23,
        head: heads,
        body: rows,
        theme: "grid",
        headStyles: {
          fillColor: [30, 58, 95],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: "bold"
        },
        bodyStyles: {
          fontSize: 7.5
        },
        styles: {
          cellPadding: 2,
          overflow: "linebreak"
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250]
        }
      };

      if (typeof autoTable === "function") {
        autoTable(doc, tableOptions);
      } else if (doc.autoTable) {
        doc.autoTable(tableOptions);
      }

      const timeTag = (this.selectedStartTime || this.selectedEndTime)
        ? `_${(this.selectedStartTime || '00-00').replace(':', '-')}_to_${(this.selectedEndTime || '23-59').replace(':', '-')}`
        : '';
      doc.save(`Tokens_Report_${dateTag}${timeTag}.pdf`);
    },
    startSlider() {
      if (this.filteredTokens.length === 0) return;
      this.showSlider = true;
      this.currentIndex = 0;
    },
    nextSlide() { this.currentIndex = (this.currentIndex + 1) % this.filteredTokens.length; },
    prevSlide() { this.currentIndex = (this.currentIndex - 1 + this.filteredTokens.length) % this.filteredTokens.length; },
    closeSlider() { this.showSlider = false; },

    /**
     * GENERATE TIME SLOTS HELPER
     * Divides a time range (e.g. 01:00 PM to 04:00 PM) into intervals of durationMinutes (e.g. 45 min)
     */
    generateTimeSlots(startTimeStr, endTimeStr, durationMinutes = 45, breakStartStr = null, breakEndStr = null) {
      try {
        const parseTimeToMinutes = (tStr) => {
          if (!tStr) return null;
          const match = String(tStr).trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
          if (!match) return null;
          let h = parseInt(match[1], 10);
          const m = parseInt(match[2], 10);
          const ampm = match[3] ? match[3].toUpperCase() : null;
          if (ampm === 'PM' && h < 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
          return h * 60 + m;
        };

        const formatMinutesTo12Hr = (totalMinutes) => {
          let h = Math.floor(totalMinutes / 60) % 24;
          const m = totalMinutes % 60;
          const ampm = h >= 12 ? 'PM' : 'AM';
          let h12 = h % 12;
          if (h12 === 0) h12 = 12;
          return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
        };

        const startMin = parseTimeToMinutes(startTimeStr);
        const endMin = parseTimeToMinutes(endTimeStr);
        if (startMin === null || endMin === null || startMin >= endMin || durationMinutes <= 0) {
          return [];
        }

        const bStartMin = parseTimeToMinutes(breakStartStr);
        const bEndMin = parseTimeToMinutes(breakEndStr);
        const hasBreak = bStartMin !== null && bEndMin !== null && bStartMin < bEndMin;

        const slots = [];
        let cur = startMin;
        while (cur + durationMinutes <= endMin) {
          const slotStart = formatMinutesTo12Hr(cur);
          const slotEnd = formatMinutesTo12Hr(cur + durationMinutes);
          const slotTime = `${slotStart} - ${slotEnd}`;
          const isBreak = hasBreak && (cur < bEndMin && (cur + durationMinutes) > bStartMin);
          slots.push({
            time: slotTime,
            is_break: Boolean(isBreak),
            is_available: !isBreak,
            booked: false
          });
          cur += durationMinutes;
        }
        return slots;
      } catch (e) {
        console.error("Error generating time slots:", e);
        return [];
      }
    },

    /**
     * LOCAL AVAILABLE SLOTS FALLBACK
     * Derives available consultation slots from matching counters or services
     */
    computeLocalAvailableSlots(serviceId = null, counterId = null) {
      const parseSlots = (raw) => {
        if (!raw) return [];
        let arr = raw;
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) arr = parsed;
            else arr = raw.split(',').map(s => s.trim()).filter(Boolean);
          } catch (e) {
            arr = raw.split(',').map(s => s.trim()).filter(Boolean);
          }
        }
        if (!Array.isArray(arr)) return [];
        return arr.map(item => {
          if (typeof item === 'string') {
            return { time: item, is_break: false, is_available: true, booked: false };
          }
          const time = item.time || item.slot || (item.start_time && item.end_time ? `${item.start_time} - ${item.end_time}` : '');
          return {
            time,
            is_break: Boolean(item.is_break || item.isBreak),
            is_available: item.is_available !== undefined ? item.is_available : !(item.is_break || item.isBreak),
            booked: Boolean(item.booked || item.is_booked)
          };
        });
      };

      const selectedService = serviceId && Array.isArray(this.servicesList)
        ? this.servicesList.find(s => String(s.id) === String(serviceId))
        : null;
      const serviceName = selectedService ? selectedService.name : '';

      // 1. Find matching counter
      let matchedCounter = null;
      if (Array.isArray(this.countersList)) {
        if (counterId) {
          matchedCounter = this.countersList.find(c => String(c.id) === String(counterId));
        }
        if (!matchedCounter && serviceId) {
          const sIdNum = Number(serviceId);
          matchedCounter = this.countersList.find(c => {
            let sIds = c.service_ids;
            while (typeof sIds === 'string') {
              try {
                const p = JSON.parse(sIds);
                if (p === sIds) break;
                sIds = p;
              } catch (e) {
                sIds = sIds.split(',').map(s => s.trim());
                break;
              }
            }
            if (Array.isArray(sIds) && sIds.map(Number).includes(sIdNum)) return true;
            if (serviceName && c.name && (c.name.toLowerCase().includes(serviceName.toLowerCase()) || serviceName.toLowerCase().includes(c.name.toLowerCase()))) {
              return true;
            }
            return false;
          });
        }
      }

      // Check counter's available_slots or start_time/end_time
      if (matchedCounter) {
        const cSlots = parseSlots(matchedCounter.available_slots || matchedCounter.slots);
        if (cSlots.length > 0) return cSlots;

        if (matchedCounter.start_time && matchedCounter.end_time) {
          const dur = parseInt(matchedCounter.slot_duration_minutes || matchedCounter.slot_duration || 45, 10);
          const gen = this.generateTimeSlots(
            matchedCounter.start_time,
            matchedCounter.end_time,
            dur,
            matchedCounter.break_start,
            matchedCounter.break_end
          );
          if (gen.length > 0) return gen;
        }
      }

      // Fallback to service's available_slots or start_time/end_time
      if (selectedService) {
        const sSlots = parseSlots(selectedService.available_slots || selectedService.slots);
        if (sSlots.length > 0) return sSlots;

        if (selectedService.start_time && selectedService.end_time) {
          const dur = parseInt(selectedService.slot_duration_minutes || selectedService.slot_duration || 45, 10);
          const gen = this.generateTimeSlots(
            selectedService.start_time,
            selectedService.end_time,
            dur,
            selectedService.break_start,
            selectedService.break_end
          );
          if (gen.length > 0) return gen;
        }
      }

      return [];
    },

    /**
     * FETCH AVAILABLE SLOTS (API + LOCAL FALLBACK)
     * Queries /v1/admin/tokens/slots or falls back to local counter/service schedule
     */
    async getAvailableSlots(serviceId = null, counterId = null, targetDate = null) {
      try {
        const params = {};
        if (counterId) params.counter_id = counterId;
        if (serviceId) params.service_id = serviceId;
        if (targetDate) params.date = targetDate;

        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/tokens/slots`, {
          params,
          ...this.getAuthHeader()
        });

        if (res.data?.success && res.data?.data) {
          const serverSlots = res.data.data.available_slots || res.data.data.slots || res.data.data;
          if (Array.isArray(serverSlots) && serverSlots.length > 0) {
            return serverSlots.map(s => {
              if (typeof s === 'string') {
                return { time: s, is_break: false, is_available: true, booked: false };
              }
              const time = s.slot || s.time || (s.start_time && s.end_time ? `${s.start_time} - ${s.end_time}` : '');
              return {
                time,
                is_break: Boolean(s.is_break || s.isBreak),
                is_available: s.is_available !== undefined ? s.is_available : !(s.is_break || s.isBreak),
                booked: Boolean(s.booked || s.is_booked)
              };
            });
          }
        }
      } catch (e) {
        // Fallback to local computation
      }

      return this.computeLocalAvailableSlots(serviceId, counterId);
    },

    /**
     * ADD/EDIT TOKEN MODAL
     * Automatically displays available token number for the selected service and target date
     */
    async handleAction(item = null, selectedService = null) {
      if (!item && !this.canAddToken) return;
      if (item && !this.canUpdateToken) return;

      const themeColor = this.getDynamicColor();
      const cleanedRole = this.userRole.toLowerCase().replace(/[^a-z0-9]/gi, '');
      const isManagementRole = ['admin', 'superadmin', 'tokenmanager', 'receptionist', 'doctor'].some(r => cleanedRole.includes(r));

      // Resolve today's date in local YYYY-MM-DD
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      let defaultBookingDate = item?.booking_date ? String(item.booking_date).substring(0, 10) : (item?.bookingDate ? String(item.bookingDate).substring(0, 10) : todayStr);

      // Ensure counter and service definitions are loaded
      if (!this.countersList || this.countersList.length === 0) {
        await this.fetchCounters();
      }
      if (!this.servicesList || this.servicesList.length === 0) {
        await this.fetchServices();
      }

      // Determine default active service ID & automatically calculate per-service 4-digit token number for defaultBookingDate
      const targetServiceId = item ? item.service_id : (selectedService ? selectedService.id : '');
      const initialService = selectedService || this.servicesList.find(s => String(s.id) === String(targetServiceId));
      const autoTokenNumber = this.generateNext4DigitTokenNumber(initialService, defaultBookingDate);
      let defaultTokenNumber = item ? item.token_number : autoTokenNumber;

      let serviceOptions = `<option value="" data-prefix="">Select a Service</option>`;
      this.servicesList.forEach(s => {
        const isSelected = String(targetServiceId) === String(s.id) ? 'selected' : '';
        serviceOptions += `<option value="${s.id}" data-prefix="${s.prefix}" ${isSelected}>${s.name} (${s.prefix})</option>`;
      });

      const isDoctor = this.isDoctor;
      const modalTitle = isDoctor 
        ? (item ? 'Edit Patient Token' : 'Add New Patient') 
        : (item ? 'Edit Token' : 'Add New Token');

      const userNameLabel = isDoctor ? 'Patient Name' : 'User Name';
      const userNamePlaceholder = isDoctor ? 'e.g. Patient Name' : 'e.g. John Doe';
      const attendedLabel = isDoctor ? 'Attended By (Doctor)' : 'Attended By (Staff)';

      const attendantsSource = this.filteredAttendantsList;

      let staffListItems = `<div class="staff-item p-2" data-value="No" data-id="" style="cursor:pointer; border-bottom:1px solid #f0f2f5;">No (Not Attended)</div>`;
      attendantsSource.forEach(a => {
        staffListItems += `<div class="staff-item p-2" data-value="${a.name}" data-id="${a.id}" style="cursor:pointer; border-bottom:1px solid #f0f2f5;">${a.name}</div>`;
      });

      let storedUserObj = {};
      try {
        storedUserObj = JSON.parse(localStorage.getItem('user') || '{}');
      } catch (e) {
        storedUserObj = {};
      }
      const currentUserId = String(storedUserObj.id || localStorage.getItem('id') || localStorage.getItem('userId') || '');
      const currentUserName = storedUserObj.name || localStorage.getItem('userName') || localStorage.getItem('name') || '';

      // Check if current logged-in user exists in attendants list
      const matchedCurrentStaff = attendantsSource.find(a => 
        (currentUserId && String(a.id) === String(currentUserId)) ||
        (currentUserName && (
          a.name.toLowerCase() === currentUserName.toLowerCase() ||
          a.name.toLowerCase().replace(/^dr\.?\s*/i, '') === currentUserName.toLowerCase().replace(/^dr\.?\s*/i, '')
        ))
      );

      const defaultAttendedName = item?.token_attended && item.token_attended !== 'No'
        ? item.token_attended
        : (matchedCurrentStaff ? matchedCurrentStaff.name : (currentUserName || 'No'));

      const defaultAttendedId = item?.user_id
        ? item.user_id
        : (matchedCurrentStaff ? matchedCurrentStaff.id : (currentUserId || ''));

      const itemStatus = String(item?.status || 'pending').toLowerCase();

      // Check whether is_emergency checkbox should be shown (Medical installation only)
      const showEmergencyField = this.isMedicalInstallation;
      const isEmergencyChecked = item && (item.is_emergency === true || item.is_emergency === 1 || item.is_emergency === 'true' || item.booking_type === 'Emergency');

      const defaultAge = item && item.age !== null && item.age !== undefined ? item.age : '';
      const defaultGender = item ? (item.gender || item.sex || '') : '';
      const defaultPhone = item && item.phone_number ? item.phone_number : '';

      // Booking Type options (matching backend API specification)
      const bookingTypeOptions = [
        "Walk-in",
        "Appointment",
        "Online",
        "Emergency",
        "VIP",
        "Teleconsultation"
      ];
      const defaultBookingType = item?.booking_type || (item?.is_emergency ? "Emergency" : "Walk-in");
      const defaultBookingTime = item?.booking_time || item?.bookingTime || "";
      const defaultDescription = item?.description || item?.notes || "";

      let bookingTypeOptionsHtml = "";
      bookingTypeOptions.forEach(opt => {
        const isSel = String(defaultBookingType).toLowerCase() === opt.toLowerCase() ? "selected" : "";
        bookingTypeOptionsHtml += `<option value="${opt}" ${isSel}>${opt}</option>`;
      });

      // Construct counter options
      const defaultCounterId = item?.counter_id !== undefined && item?.counter_id !== null ? String(item.counter_id) : (item?.counter?.id ? String(item.counter.id) : "");
      let counterOptionsHtml = `<option value="">-- Auto-assign Counter --</option>`;
      (this.countersList || []).forEach(cnt => {
        const isSel = String(defaultCounterId) === String(cnt.id) ? "selected" : "";
        counterOptionsHtml += `<option value="${cnt.id}" ${isSel}>${cnt.name}</option>`;
      });

      // Compute initial available consultation slots for selected service / counter
      const initialSlots = await this.getAvailableSlots(targetServiceId, item?.counter_id, defaultBookingDate);

      let slotOptionsHtml = `<option value="">-- Select an Available Consultation Slot --</option>`;
      let hasSelectedSlot = false;
      initialSlots.forEach(slot => {
        const slotTime = typeof slot === 'string' ? slot : slot.time;
        const isBreak = typeof slot === 'object' && Boolean(slot.is_break);
        const isSel = String(defaultBookingTime).trim().toLowerCase() === String(slotTime).trim().toLowerCase();
        if (isSel) hasSelectedSlot = true;
        slotOptionsHtml += `<option value="${slotTime}" ${isSel ? 'selected' : ''} ${isBreak ? 'disabled class="text-muted bg-light"' : ''}>${slotTime}${isBreak ? ' (Break - Unavailable)' : ''}</option>`;
      });

      if (defaultBookingTime && !hasSelectedSlot) {
        slotOptionsHtml += `<option value="${defaultBookingTime}" selected>${defaultBookingTime} (Current / Custom)</option>`;
      }
      slotOptionsHtml += `<option value="__custom__">-- Enter Custom Time --</option>`;

      // DIRECTLY OPEN THE EDIT MODAL (No pre-alerts before modal opens)
      const { value: formValues } = await Swal.fire({
        title: modalTitle,
        width: '850px',
        html: `<div class="text-start">
            <div class="row g-2 mb-3">
              <div class="col-md-6">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Select Service</label>
                <select id="swal-service-id" class="form-select">
                  ${serviceOptions}
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Available Token Number for Selected Service</label>
                <input id="swal-token-num" class="form-control" placeholder="Select service to view available token" value="${defaultTokenNumber}">
              </div>
            </div>
            
            <div class="row g-2 mb-3">
              <div class="col-md-5 col-12">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">${userNameLabel}</label>
                <input id="swal-token-name" class="form-control" placeholder="${userNamePlaceholder}" value="${item ? (item.token_name || '') : ''}">
              </div>
              <div class="col-md-2 col-4">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Age</label>
                <input id="swal-token-age" type="number" class="form-control" placeholder="e.g. 25" value="${defaultAge}">
              </div>
              <div class="col-md-2 col-4">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Gender</label>
                <select id="swal-token-gender" class="form-select">
                  <option value="" ${!defaultGender ? 'selected' : ''}>Select Gender</option>
                  <option value="Male" ${String(defaultGender).toLowerCase() === 'male' ? 'selected' : ''}>Male</option>
                  <option value="Female" ${String(defaultGender).toLowerCase() === 'female' ? 'selected' : ''}>Female</option>
                  <option value="Other" ${String(defaultGender).toLowerCase() === 'other' ? 'selected' : ''}>Other</option>
                </select>
              </div>
              <div class="col-md-3 col-4">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Phone Number</label>
                <input id="swal-token-phone" type="text" class="form-control" placeholder="e.g. 9876543210" value="${defaultPhone}">
              </div>
            </div>

            <div class="row g-2 mb-3">
              <div class="col-md-4 col-12">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Assign Counter</label>
                <select id="swal-token-counter-id" class="form-select">
                  ${counterOptionsHtml}
                </select>
              </div>
              <div class="col-md-4 col-12">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Booking Type</label>
                <select id="swal-token-booking-type" class="form-select">
                  ${bookingTypeOptionsHtml}
                </select>
              </div>
              <div class="col-md-4 col-12">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Booking Date</label>
                <input id="swal-token-booking-date" type="date" class="form-control" value="${defaultBookingDate}">
              </div>
            </div>

            <div class="row g-2 mb-3">
              <div class="col-12">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary d-flex justify-content-between align-items-center mb-1">
                  <span><i class="far fa-clock me-1 text-info"></i> BOOKING TIME / SLOT</span>
                  <span class="text-xxs text-muted fw-normal">Slots during break are disabled</span>
                </label>
                <select id="swal-token-booking-time" class="form-select">
                  ${slotOptionsHtml}
                </select>
                <div id="swal-custom-time-wrapper" class="mt-2" style="display: ${defaultBookingTime && !hasSelectedSlot && !initialSlots.some(s => (typeof s === 'string' ? s : s.time) === defaultBookingTime) ? 'block' : 'none'};">
                  <input 
                    id="swal-token-custom-time" 
                    type="text" 
                    class="form-control form-control-sm" 
                    placeholder="e.g. 10:30 AM or 14:00" 
                    autocomplete="off"
                    value="${defaultBookingTime}"
                  >
                  <small class="text-muted text-xxs">Custom consultation time entered manually</small>
                </div>
              </div>
            </div>

            ${isManagementRole ? `
              <div class="row g-2 mb-3">
                <div class="col-md-6">
                  <label class="form-label text-xs fw-bold text-uppercase text-secondary">${attendedLabel}</label>
                  <div class="position-relative">
                    <div class="input-group">
                      <span class="input-group-text bg-white border-end-0"><i class="fas fa-search text-xs text-secondary"></i></span>
                      <input type="text" id="swal-attendant-search" class="form-control border-start-0" placeholder="Search doctor..." autocomplete="off" value="${defaultAttendedName}">
                      <span class="input-group-text bg-white"><i class="fas fa-chevron-down text-xxs"></i></span>
                    </div>
                    <input type="hidden" id="swal-token-attended" value="${defaultAttendedName || 'No'}">
                    <input type="hidden" id="swal-token-attended-id" value="${defaultAttendedId || ''}">
                    
                    <div id="staff-dropdown-list" class="shadow-lg border rounded bg-white w-100" style="display:none; position:absolute; z-index:9999; max-height:200px; overflow-y:auto; top:100%; left:0;">
                      ${staffListItems}
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="form-label text-xs fw-bold text-uppercase text-secondary">Status</label>
                  <select id="swal-token-status" class="form-select">
                    <option value="pending" ${itemStatus === 'pending' ? 'selected' : ''}>Waiting</option>
                    <option value="completed" ${itemStatus === 'completed' ? 'selected' : ''}>Completed</option>
                    <option value="skipped" ${itemStatus === 'skipped' ? 'selected' : ''}>Skipped</option>
                    <option value="rejected" ${itemStatus === 'rejected' ? 'selected' : ''}>Rejected</option>
                  </select>
                </div>
              </div>
            ` : `
              <input type="hidden" id="swal-token-attended" value="${defaultAttendedName || 'No'}">
              <input type="hidden" id="swal-token-attended-id" value="${defaultAttendedId || ''}">
              <input type="hidden" id="swal-token-status" value="pending">
            `}

            <div class="row g-2 mb-2">
              <div class="${showEmergencyField ? 'col-md-7' : 'col-12'}">
                <label class="form-label text-xs fw-bold text-uppercase text-secondary">Chief Complaint / Description</label>
                <textarea id="swal-token-description" class="form-control" rows="2" placeholder="e.g. Routine checkup and persistent dry cough">${defaultDescription}</textarea>
              </div>
              ${showEmergencyField ? `
                <div class="col-md-5 d-flex align-items-end">
                  <div class="form-check form-switch p-2 rounded bg-light border w-100 mb-0">
                    <input class="form-check-input ms-0 me-2" type="checkbox" id="swal-token-emergency" ${isEmergencyChecked ? 'checked' : ''} style="cursor: pointer;">
                    <label class="form-check-label text-xs fw-bold text-danger text-uppercase mb-0" for="swal-token-emergency" style="cursor: pointer;">
                      <i class="fas fa-ambulance me-1"></i> Emergency Patient (High Priority)
                    </label>
                  </div>
                </div>
              ` : `
                <input type="hidden" id="swal-token-emergency" value="${isEmergencyChecked ? 'true' : 'false'}">
              `}
            </div>
          </div>`,
        showCancelButton: true,
        confirmButtonColor: themeColor,
        didOpen: () => {
          const serviceSelect = document.getElementById('swal-service-id');
          const tokenNumInput = document.getElementById('swal-token-num');
          const tokenNameInput = document.getElementById('swal-token-name');
          const bookingDateInput = document.getElementById('swal-token-booking-date');
          const bookingTimeSelect = document.getElementById('swal-token-booking-time');
          const customTimeWrapper = document.getElementById('swal-custom-time-wrapper');
          const customTimeInput = document.getElementById('swal-token-custom-time');

          // Place focus on User Name input when triggered via tile click
          if (selectedService) {
            setTimeout(() => tokenNameInput.focus(), 100);
          }

          if (bookingTimeSelect) {
            bookingTimeSelect.addEventListener('change', (e) => {
              const val = e.target.value;
              if (val === '__custom__') {
                if (customTimeWrapper) customTimeWrapper.style.display = 'block';
                if (customTimeInput) customTimeInput.focus();
              } else {
                if (customTimeWrapper) customTimeWrapper.style.display = 'none';
              }
            });
          }

          // DYNAMIC RECALCULATION: Triggered when Service OR Booking Date changes
          const updateTokenNumberForDate = async () => {
            const sId = serviceSelect ? serviceSelect.value : '';
            const bDate = bookingDateInput ? (bookingDateInput.value || todayStr) : todayStr;
            const foundService = this.servicesList.find(s => String(s.id) === String(sId));

            // If editing an item, keep original token number unless user altered date/service
            if (item) {
              const origDate = item.booking_date ? String(item.booking_date).substring(0, 10) : todayStr;
              if (bDate === origDate && String(sId) === String(item.service_id)) {
                const targetNumInput = document.getElementById('swal-token-num');
                if (targetNumInput) targetNumInput.value = item.token_number;
                return;
              }
            }

            if (foundService) {
              const nextNum = await this.resolveNextTokenNumber(foundService, bDate);
              const targetNumInput = document.getElementById('swal-token-num');
              if (targetNumInput) targetNumInput.value = nextNum;
            } else {
              const targetNumInput = document.getElementById('swal-token-num');
              if (targetNumInput) targetNumInput.value = '';
            }
          };

          // DYNAMIC CONSULTATION SLOTS REFRESH: Derives/fetches slots when counter/service or date changes
          const updateSlotsForSelection = async () => {
            const currentEl = document.getElementById('swal-token-booking-time');
            const customEl = document.getElementById('swal-token-custom-time');
            const wrapperEl = document.getElementById('swal-custom-time-wrapper');
            if (!currentEl) return;

            const sId = serviceSelect ? serviceSelect.value : '';
            const bDate = bookingDateInput ? (bookingDateInput.value || todayStr) : todayStr;
            const currentVal = (currentEl.value === '__custom__' && customEl)
              ? customEl.value
              : currentEl.value;

            const slots = await this.getAvailableSlots(sId, item?.counter_id, bDate);

            // Re-fetch element after await to prevent ESLint require-atomic-updates error
            const targetSelect = document.getElementById('swal-token-booking-time');
            if (!targetSelect) return;

            let opts = `<option value="">-- Select an Available Consultation Slot --</option>`;
            let matched = false;
            slots.forEach(slot => {
              const slotTime = typeof slot === 'string' ? slot : slot.time;
              const isBreak = typeof slot === 'object' && Boolean(slot.is_break);
              const isSel = currentVal && String(currentVal).trim().toLowerCase() === String(slotTime).trim().toLowerCase();
              if (isSel) matched = true;
              opts += `<option value="${slotTime}" ${isSel ? 'selected' : ''} ${isBreak ? 'disabled class="text-muted bg-light"' : ''}>${slotTime}${isBreak ? ' (Break - Unavailable)' : ''}</option>`;
            });

            if (currentVal && !matched && currentVal !== '__custom__') {
              opts += `<option value="${currentVal}" selected>${currentVal} (Current / Custom)</option>`;
            }
            opts += `<option value="__custom__">-- Enter Custom Time --</option>`;
            targetSelect.innerHTML = opts;

            if (wrapperEl) {
              wrapperEl.style.display = targetSelect.value === '__custom__' ? 'block' : 'none';
            }
          };

          // Auto-update token number and consultation slots when service dropdown or booking date changes
          serviceSelect.addEventListener('change', async () => {
            await updateTokenNumberForDate();
            await updateSlotsForSelection();
          });
          if (bookingDateInput) {
            bookingDateInput.addEventListener('change', async () => {
              await updateTokenNumberForDate();
              await updateSlotsForSelection();
            });
            bookingDateInput.addEventListener('input', async () => {
              await updateTokenNumberForDate();
              await updateSlotsForSelection();
            });
          }

          // Enforce 4-digit formatting on custom edits
          if (tokenNumInput) {
            tokenNumInput.addEventListener('blur', (e) => {
              const val = e.target.value;
              const digits = val.replace(/\D/g, '');
              if (digits) {
                const padded = String(digits).padStart(4, '0');
                e.target.value = val.replace(/\d+$/, padded);
              }
            });
          }

          const searchInput = document.getElementById('swal-attendant-search');
          if (searchInput) {
            const dropdownList = document.getElementById('staff-dropdown-list');
            const hiddenName = document.getElementById('swal-token-attended');
            const hiddenId = document.getElementById('swal-token-attended-id');
            const items = dropdownList.querySelectorAll('.staff-item');

            const syncAttendantFromInput = () => {
              const typed = (searchInput.value || '').trim();
              if (!typed || typed.toLowerCase() === 'no') {
                hiddenName.value = 'No';
                hiddenId.value = '';
                return;
              }
              const cleanTyped = typed.toLowerCase().replace(/^dr\.?\s*/i, '').trim();
              const foundStaff = attendantsSource.find(a => {
                const aClean = a.name.toLowerCase().replace(/^dr\.?\s*/i, '').trim();
                return aClean === cleanTyped || aClean.includes(cleanTyped) || cleanTyped.includes(aClean);
              });
              if (foundStaff) {
                hiddenName.value = foundStaff.name;
                hiddenId.value = String(foundStaff.id);
              } else {
                hiddenName.value = typed;
              }
            };

            searchInput.addEventListener('focus', () => { dropdownList.style.display = 'block'; });
            searchInput.addEventListener('blur', () => {
              setTimeout(() => {
                syncAttendantFromInput();
                dropdownList.style.display = 'none';
              }, 150);
            });

            searchInput.addEventListener('input', (e) => {
              const term = e.target.value.toLowerCase();
              dropdownList.style.display = 'block';
              let foundCount = 0;
              items.forEach(item => {
                const text = item.innerText.toLowerCase();
                if(text.includes(term)) {
                  item.style.display = 'block';
                  foundCount++;
                } else {
                  item.style.display = 'none';
                }
              });
              if(foundCount === 0) dropdownList.style.display = 'none';
            });

            dropdownList.addEventListener('mousedown', (e) => {
              const target = e.target.closest('.staff-item');
              if (target) {
                const name = target.getAttribute('data-value');
                const id = target.getAttribute('data-id');
                searchInput.value = name === 'No' ? '' : name;
                hiddenName.value = name;
                hiddenId.value = id;
                dropdownList.style.display = 'none';
              }
            });

            document.addEventListener('mousedown', (e) => {
              if (!searchInput.contains(e.target) && !dropdownList.contains(e.target)) {
                dropdownList.style.display = 'none';
              }
            });
          }

          const bookingTypeSelect = document.getElementById('swal-token-booking-type');
          const emergencyEl = document.getElementById('swal-token-emergency');
          if (bookingTypeSelect && emergencyEl) {
            bookingTypeSelect.addEventListener('change', async (e) => {
              if (e.target.value === 'Emergency') {
                if (emergencyEl.type === 'checkbox') emergencyEl.checked = true;
                else emergencyEl.value = 'true';
              }
            });
            if (emergencyEl.type === 'checkbox') {
              emergencyEl.addEventListener('change', async (e) => {
                if (e.target.checked) {
                  if (bookingTypeSelect.value === 'Walk-in') {
                    bookingTypeSelect.value = 'Emergency';
                  }
                }
              });
            }
          }
        },
        preConfirm: () => {
          const service_id = document.getElementById('swal-service-id').value;
          const name = document.getElementById('swal-token-name').value;
          let number = document.getElementById('swal-token-num').value;
          const searchInputEl = document.getElementById('swal-attendant-search');
          let attended = document.getElementById('swal-token-attended')?.value || 'No';
          let user_id = document.getElementById('swal-token-attended-id')?.value || '';
          const status = (document.getElementById('swal-token-status').value || 'pending').toLowerCase();
          const ageVal = document.getElementById('swal-token-age')?.value;
          const genderVal = document.getElementById('swal-token-gender')?.value || null;
          const phoneVal = document.getElementById('swal-token-phone')?.value;
          const booking_type = document.getElementById('swal-token-booking-type')?.value || 'Walk-in';
          const booking_date = document.getElementById('swal-token-booking-date')?.value || null;
          let booking_time = null;
          const bookingTimeEl = document.getElementById('swal-token-booking-time');
          const customTimeEl = document.getElementById('swal-token-custom-time');
          if (bookingTimeEl) {
            if (bookingTimeEl.value === '__custom__') {
              booking_time = customTimeEl?.value || null;
            } else {
              booking_time = bookingTimeEl.value || null;
            }
          }
          if (booking_time && typeof booking_time === 'string') {
            booking_time = booking_time.trim();
            if (!booking_time) booking_time = null;
          }
          const descriptionVal = document.getElementById('swal-token-description')?.value;
          const description = descriptionVal && descriptionVal.trim() ? descriptionVal.trim() : null;

          // If attendant search input has value but hidden ID is not matched, resolve now
          if (searchInputEl && searchInputEl.value && (!user_id || attended === 'No')) {
            const cleanTyped = searchInputEl.value.trim().toLowerCase().replace(/^dr\.?\s*/i, '').trim();
            const foundStaff = attendantsSource.find(a => {
              const aClean = a.name.toLowerCase().replace(/^dr\.?\s*/i, '').trim();
              return aClean === cleanTyped || aClean.includes(cleanTyped) || cleanTyped.includes(aClean);
            });
            if (foundStaff) {
              attended = foundStaff.name;
              user_id = String(foundStaff.id);
            } else if (searchInputEl.value.trim()) {
              attended = searchInputEl.value.trim();
            }
          }

          // If logged-in user is a doctor or attendant, default to them if not explicitly set to another
          if ((!user_id || attended === 'No') && (isDoctor || currentUserId)) {
            if (currentUserName && currentUserName.toLowerCase() !== 'admin') {
              attended = currentUserName;
              user_id = currentUserId || null;
            }
          }

          // Resolve emergency value safely
          const emergencyEl = document.getElementById('swal-token-emergency');
          let is_emergency = false;
          if (emergencyEl) {
            is_emergency = emergencyEl.type === 'checkbox' ? emergencyEl.checked : (emergencyEl.value === 'true');
          }

          // Only Service and Token Number are compulsory; User Name (Counter) is optional for Queue tickets!
          if (!number || !service_id) return Swal.showValidationMessage('Please select a service');

          const userStr = localStorage.getItem('user') || '{}';
          let user = {};
          try {
            user = JSON.parse(userStr);
          } catch (e) {
            console.debug("User parse error in preConfirm:", e);
          }
          const tenant_id = user.tenant_id || user.tenantId || localStorage.getItem('tenant_id') || localStorage.getItem('tenantId') || null;
          const branch_id = user.branch_id || user.branchId || localStorage.getItem('selectedBranchId') || localStorage.getItem('branch_id') || null;

          // Resolve counter_id based on explicit counter selection or assigned doctor/attendant
          let resolvedCounterId = item ? item.counter_id : null;
          const chosenCounterEl = document.getElementById('swal-token-counter-id');
          if (chosenCounterEl && chosenCounterEl.value) {
            resolvedCounterId = Number(chosenCounterEl.value);
          }
          let resolvedUserId = user_id || null;
          let resolvedAttended = attended || 'No';

          // If a doctor/user is assigned to this token, associate with their counter if not explicitly set
          if (!resolvedCounterId && resolvedUserId && Array.isArray(this.countersList)) {
            const userCounter = this.countersList.find(c => 
              String(c.user_id || (c.user ? c.user.id : '')) === String(resolvedUserId)
            );
            if (userCounter) {
              resolvedCounterId = userCounter.id;
            }
          }

          // If still no counter and the logged-in doctor/user has an active counter in localStorage
          if (!resolvedCounterId && (isDoctor || String(resolvedUserId) === String(currentUserId))) {
            const myStoredCounter = localStorage.getItem('counter_id');
            if (myStoredCounter) {
              resolvedCounterId = Number(myStoredCounter);
            }
          }

          const isEmer = this.isMedicalInstallation ? Boolean(is_emergency) : (booking_type === 'Emergency');

          const payload = {
            service_id: Number(service_id),
            token_name: name || null,
            token_number: number,
            token_attended: resolvedAttended || 'No',
            user_id: resolvedUserId || null,
            counter_id: resolvedCounterId ? Number(resolvedCounterId) : null,
            booking_type: booking_type || 'Walk-in',
            booking_date: booking_date || null,
            booking_time: booking_time ? booking_time.trim() : null,
            description: description || null,
            status,
            is_emergency: isEmer,
            age: ageVal !== undefined && ageVal !== '' && !isNaN(ageVal) ? parseInt(ageVal, 10) : null,
            gender: genderVal ? genderVal.trim() : null,
            phone_number: phoneVal ? phoneVal.trim() : null
          };
          if (item) {
            payload.updatedAt = new Date().toISOString();
            payload.updated_at = new Date().toISOString();
          }
          if (tenant_id) payload.tenant_id = tenant_id;
          if (branch_id) payload.branch_id = branch_id;
          return payload;
        }
      });

      if (formValues) {
        let finalTokenNumber = formValues.token_number;
        let finalBookingDate = formValues.booking_date;

        // =========================================================================
        // DATE CHANGE & RE-SEQUENCING CONFIRMATION CHECK
        // If editing an existing token and the booking date was modified
        // =========================================================================
        if (item) {
          const origBookingDate = item.booking_date ? String(item.booking_date).substring(0, 10) : '';
          const newBookingDate = formValues.booking_date ? String(formValues.booking_date).substring(0, 10) : '';
          const isEmergency = formValues.is_emergency || item.is_emergency;

          if (newBookingDate && origBookingDate && newBookingDate !== origBookingDate) {
            const targetService = this.servicesList.find(s => String(s.id) === String(formValues.service_id)) || initialService;
            
            // Resolve next sequence number for this date (e.g., if X-0001 to X-0010 exist, returns X-0011)
            const nextAvailableNum = await this.resolveNextTokenNumber(targetService, newBookingDate);

            // Date formatting helper (DD-MM-YYYY)
            const formatD = (dStr) => {
              if (!dStr) return '';
              const parts = dStr.split('-');
              return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dStr;
            };

            const displayOrigDate = formatD(origBookingDate);
            const displayNewDate = formatD(newBookingDate);
            const isMovingToToday = newBookingDate === todayStr;

            const confirmResult = await Swal.fire({
              title: isEmergency ? 'Emergency Token Detected' : 'Confirm Booking Date Change',
              html: `
                <div class="text-start">
                  <p class="mb-2">
                    This token <strong>${item.token_number}</strong> is scheduled for that particular date: 
                    <span class="badge bg-danger text-white">${displayOrigDate}</span>.
                  </p>
                  <p class="text-xs text-secondary mb-2">
                    ${isMovingToToday ? "Today's queue on this counter already has tokens (e.g. X-0001, X-0002)." : `Tokens are already present on ${displayNewDate}.`}
                  </p>
                  <div class="p-2 mb-2 rounded bg-light border">
                    <p class="text-xs text-dark font-weight-bold mb-0">
                      If moved to ${isMovingToToday ? `Today (<strong>${displayNewDate}</strong>)` : `<strong>${displayNewDate}</strong>`}, the next available token number will be:
                      <span class="badge bg-success ms-1">${nextAvailableNum}</span>
                    </p>
                  </div>
                  <p class="text-xs text-dark font-weight-bold mb-0">
                    Before updating, do you give permission to change the date to ${isMovingToToday ? `Today (${displayNewDate})` : displayNewDate} and reassign the token number to <strong>${nextAvailableNum}</strong>?
                  </p>
                </div>
              `,
              icon: 'warning',
              showCancelButton: true,
              showDenyButton: true,
              confirmButtonText: `<i class="fas fa-check-circle me-1"></i> Yes, Move to ${isMovingToToday ? 'Today' : displayNewDate} (${nextAvailableNum})`,
              denyButtonText: `<i class="fas fa-calendar-alt me-1"></i> Keep Original Date (${displayOrigDate})`,
              cancelButtonText: 'Cancel',
              confirmButtonColor: '#2dce89',
              denyButtonColor: '#172b4d',
              cancelButtonColor: '#8898aa',
              reverseButtons: false
            });

            // If user cancels or dismisses, abort the update
            if (confirmResult.isDismissed) {
              return;
            }

            if (confirmResult.isConfirmed) {
              finalBookingDate = newBookingDate;
              finalTokenNumber = nextAvailableNum;
            } else if (confirmResult.isDenied) {
              // User opted to keep the original date and token number
              finalBookingDate = origBookingDate;
              finalTokenNumber = item.token_number;
            }
          }
        }

        const payloadToSend = {
          ...formValues,
          name: formValues.token_name,
          token_name: formValues.token_name,
          phone: formValues.phone_number,
          phone_number: formValues.phone_number,
          notes: formValues.description,
          description: formValues.description,
          booking_date: finalBookingDate,
          token_number: finalTokenNumber
        };

        // If counter was altered, notify transfer-counter
        if (item && payloadToSend.counter_id && String(item.counter_id) !== String(payloadToSend.counter_id)) {
          await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/transfer-counter`, {
            tokenId: item.id,
            counterId: Number(payloadToSend.counter_id),
            status: payloadToSend.status
          }, this.getAuthHeader()).catch(() => {});
        }

        try {
          const url = item ? `${this.apiBaseUrl}/v1/admin/tokens/update/${item.id}` : `${this.apiBaseUrl}/v1/admin/tokens/add`;
          let res;
          try {
            res = await axios[item ? 'put' : 'post'](url, payloadToSend, this.getAuthHeader());
          } catch (e1) {
            if (item) {
              try {
                res = await axios.post(`${this.apiBaseUrl}/v1/admin/tokens/update/${item.id}`, payloadToSend, this.getAuthHeader());
              } catch (e2) {
                res = await axios.put(`${this.apiBaseUrl}/v1/admin/tokens/${item.id}`, payloadToSend, this.getAuthHeader());
              }
            } else {
              res = await axios.post(`${this.apiBaseUrl}/v1/admin/tokens`, payloadToSend, this.getAuthHeader());
            }
          }
          localStorage.setItem('tokens_last_updated', String(Date.now()));
          window.dispatchEvent(new CustomEvent('token-created', { detail: res?.data || payloadToSend }));
          window.dispatchEvent(new CustomEvent('tokens-updated', { detail: res?.data || payloadToSend }));
          await this.fetchTokens(); 
          Swal.fire({ title: 'Success', icon: 'success', confirmButtonColor: themeColor });
        } catch (error) { 
          const errorMsg = error.response?.data?.message || 'Operation failed.';
          Swal.fire({ 
            title: 'Action Restricted', 
            text: errorMsg, 
            icon: 'error', 
            confirmButtonColor: themeColor 
          }); 
        }
      }
    },

    async deleteToken(id) {
      if (!this.canDeleteToken) return;
      const themeColor = this.getDynamicColor();
      const result = await Swal.fire({ title: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' });
      if (result.isConfirmed) {
        try {
          await axios.delete(`${this.apiBaseUrl}/v1/admin/tokens/delete/${id}`, this.getAuthHeader());
          localStorage.setItem('tokens_last_updated', String(Date.now()));
          window.dispatchEvent(new CustomEvent('tokens-updated', { detail: { id } }));
          this.fetchTokens();
          Swal.fire({ title: 'Deleted!', icon: 'success', confirmButtonColor: themeColor });
        } catch (error) { Swal.fire({ title: 'Error', icon: 'error', confirmButtonColor: themeColor }); }
      }
    },
    getBookingTypeBadgeClass(type) {
      const t = String(type || '').toLowerCase();
      if (t === 'emergency') return 'bg-danger text-white';
      if (t === 'vip') return 'bg-warning text-dark font-weight-bold';
      if (t === 'appointment') return 'bg-info text-white';
      if (t === 'online' || t === 'teleconsultation') return 'bg-primary text-white';
      return 'bg-light text-secondary border';
    }
  },
  mounted() { 
    this.detectRole();
    const roleCleaned = (this.userRole || '').toLowerCase().replace(/[^a-z0-9]/gi, '');
    if (roleCleaned === 'tokenuser' || roleCleaned === 'operator' || roleCleaned.includes('tokenuser') || roleCleaned.includes('operator')) {
      if (this.currentView === 'board') {
        this.currentView = 'tokens';
      }
    }
    this.fetchServices();
    this.fetchAttendants();
    this.fetchCounters();
    this.fetchTokens(); 
    this.checkServingStatus();

    // Listen to token creation / update events from Admin or other components
    this.onTokenCreatedHandler = () => {
      this.fetchTokens();
    };
    window.addEventListener('token-created', this.onTokenCreatedHandler);
    window.addEventListener('tokens-updated', this.onTokenCreatedHandler);

    // Sync across browser tabs/windows when an Admin adds tokens in another tab
    this.onStorageChangeHandler = (e) => {
      if (e && e.key === 'tokens_last_updated') {
        this.fetchTokens();
      }
    };
    window.addEventListener('storage', this.onStorageChangeHandler);

    // Auto-refresh queue tokens periodically (every 10s) to keep live tokens visible for all users
    this.tokenRefreshInterval = setInterval(() => {
      this.fetchTokens();
    }, 10000);
  },
  beforeUnmount() {
    if (this.onTokenCreatedHandler) {
      window.removeEventListener('token-created', this.onTokenCreatedHandler);
      window.removeEventListener('tokens-updated', this.onTokenCreatedHandler);
    }
    if (this.onStorageChangeHandler) {
      window.removeEventListener('storage', this.onStorageChangeHandler);
    }
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }
  }
};
</script>

<style scoped>
/* SERVICE CARD TILE STYLES */
.service-card {
  border-radius: 18px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25) !important;
}

.icon-box-centered {
  background-color: rgba(255, 255, 255, 0.15);
  width: 55px;
  height: 55px;
  border-radius: 12px;
  transition: background 0.3s ease;
}

.icon-size {
  font-size: 1.4rem !important;
  opacity: 1 !important;
}

.tile-body {
  width: 100%;
}

.inactive-tile {
  background: #5e6c7b !important;
  opacity: 0.7;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: 1px solid white;
}

/* TOKEN SLIDER OVERLAY STYLES */
.token-slider-overlay {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: radial-gradient(circle, var(--theme-bg) 0%, #000000 100%);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
  overflow-y: auto; 
}
.theme-accent-bg { background-color: var(--theme-primary) !important; color: white !important; }
.theme-accent-text { color: var(--theme-primary) !important; }
.theme-card-bg { background: var(--theme-card) !important; backdrop-filter: blur(10px); }
.main-card-border { border: 1px solid var(--theme-primary) !important; box-shadow: 0 10px 30px rgba(0,0,0,0.5); }
.slider-wrapper { display: flex; align-items: center; width: 100%; padding: 0 40px; height: 80vh; }
.slider-main-content { flex: 1; height: 100%; display: flex; align-items: center; }
.token-display-number { font-size: 15rem; font-weight: 900; color: #fff; margin: 0; line-height: 0.9; text-shadow: 0 0 50px rgba(255, 255, 255, 0.2); }
.token-display-name { font-size: 3.5rem; color: #8898aa; text-transform: uppercase; letter-spacing: 8px; margin-top: 10px;}
.border-end-divider { border-right: 1px solid rgba(255, 255, 255, 0.08); }
.side-info-card { border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 15px; padding: 20px; }
.card-label { font-size: 0.7rem; font-weight: 800; color: #adb5bd; letter-spacing: 1px; }
.side-token-num { font-size: 2.5rem; font-weight: 700; color: #fff; margin: 0; }
.side-token-nm { font-size: 1rem; color: #6c757d; text-transform: uppercase; }
.next-token-card { border-radius: 20px; padding: 35px; }
.next-header { font-size: 0.85rem; font-weight: 800; margin-bottom: 20px; letter-spacing: 2px; }
.next-token-num { font-size: 6rem; font-weight: 800; color: #fff; margin: 0; line-height: 1; }
.next-token-nm { font-size: 1.8rem; color: #cbd5e0; text-transform: uppercase; margin-top: 5px; }
.nav-btn { background: rgba(255, 255, 255, 0.03); border: none; color: #fff; width: 50px; height: 50px; border-radius: 50%; cursor: pointer; transition: 0.3s; }
.nav-btn:hover { background: var(--theme-primary); transform: scale(1.1); }
.btn-close-slider { position: absolute; top: 30px; right: 30px; background: none; border: none; color: rgba(255,255,255,0.3); font-size: 24px; cursor: pointer; transition: 0.3s; }
.btn-close-slider:hover { color: #fff; }
.btn-back-slider { position: absolute; top: 30px; left: 30px; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 8px 20px; border-radius: 30px; cursor: pointer; transition: 0.3s; font-size: 0.8rem; font-weight: 700; letter-spacing: 1px; }
.btn-back-slider:hover { background: var(--theme-primary); transform: translateX(-5px); border-color: var(--theme-primary); }
.ls-1 { letter-spacing: 2px; }

.status-counters-wrapper { position: absolute; top: 25px; right: 80px; display: flex; gap: 12px; }
.status-counter-item { background: rgba(255, 255, 255, 0.12); border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 12px; padding: 8px 18px; text-align: center; min-width: 100px; backdrop-filter: blur(8px); }
.sc-value { font-size: 1.4rem; font-weight: 800; line-height: 1; text-shadow: 0 0 10px rgba(0,0,0,0.5); }
.sc-label { font-size: 0.65rem; font-weight: 800; color: #ffffff; margin-top: 5px; letter-spacing: 1px; opacity: 0.9; }

.border-success-soft { border-top: 3px solid #2dce89 !important; }
.border-warning-soft { border-top: 3px solid #fb6340 !important; }
.border-secondary-soft { border-top: 3px solid #8898aa !important; }
.border-danger-soft { border-top: 3px solid #f5365c !important; }

.staff-item:hover, .token-search-item:hover { background-color: #f8f9fa !important; color: var(--theme-primary); font-weight: bold; }

/* KANBAN COUNTER BOARD STYLING */
.counter-board-wrapper {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  align-items: flex-start;
  min-height: 520px;
}

.counter-column {
  flex: 0 0 280px;
  background: #f8f9fe;
  border-radius: 12px;
  border: 2px dashed #d2d6da;
  transition: all 0.2s ease;
  min-height: 480px;
}

.counter-column.drop-active {
  background: #e9ecef;
  border-color: #5e72e4 !important;
  transform: scale(1.02);
}

.counter-column-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.counter-column-body {
  max-height: 450px;
  overflow-y: auto;
}

.token-drag-card {
  cursor: grab;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  user-select: none;
}

.token-drag-card:active {
  cursor: grabbing;
}

.token-drag-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}

.draggable-row {
  cursor: grab;
}

.draggable-row:active {
  cursor: grabbing;
}

/* Highlight styling for yesterday's carryover tokens */
.yesterday-token-highlight {
  background-color: #fffdf5 !important;
  border-left: 4px solid #f59e0b !important;
}
.yesterday-token-highlight:hover {
  background-color: #fef9c3 !important;
}

@media (max-width: 991.98px) {
  .token-slider-overlay { padding-top: 100px; padding-bottom: 50px; align-items: flex-start; }
  .slider-wrapper { flex-direction: column; height: auto; padding: 0 20px; }
  .slider-main-content { width: 100%; }
  .token-display-number { font-size: 7rem; }
  .token-display-name { font-size: 2rem; letter-spacing: 3px; }
  .border-end-divider { border-right: none; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 20px; margin-bottom: 20px; }
  .status-counters-wrapper { top: 70px; right: 20px; left: 20px; justify-content: center; flex-wrap: wrap; }
  .status-counter-item { min-width: 80px; padding: 5px 10px; }
  .sc-value { font-size: 1.1rem; }
  .next-token-num { font-size: 3.5rem; }
  .next-token-nm { font-size: 1.4rem; }
  .nav-btn { width: 40px; height: 40px; }
  .btn-back-slider, .btn-close-slider { top: 15px; }
}
@media (max-width: 768px) { .card-header { padding-bottom: 20px !important; } }
.bg-warning-soft {
  background-color: rgba(245, 158, 11, 0.15) !important;
}
</style>